import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../notifications';
import { Breadcrumb, BreadcrumbItem, Spinner, ToggleField } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import {
  TelegramTestSendResult,
  TelegramWebhookInfo,
  UpdateTelegramSettingsRequest,
  UpdateWhatsAppSettingsRequest,
  WhatsAppTestSendResult,
  UpdateInstagramSettingsRequest,
  InstagramSettings,
  InstagramTestSendResult,
  InstagramWebhookSubscription,
  InstagramWebhookSubscriptionStatus,
} from '../../companies/types/Company';
import {
  getTelegramWebhookInfo,
  getInstagramSettings,
  getInstagramWebhookSubscriptions,
  registerTelegramWebhook,
  subscribeInstagramWebhooks,
  unsubscribeInstagramWebhooks,
  updateInstagramSettings,
  startInstagramOAuth,
} from '../../companies/services/companyService';
import { WebChatConnectorTab } from './WebChatConnectorTab';
import { MessengerConnectorTab } from './MessengerConnectorTab';
import { TikTokConnectorTab } from './TikTokConnectorTab';
import { CallsConnectorTab } from './CallsConnectorTab';
import { SmsConnectorTab } from './SmsConnectorTab';
import { ChannelAccountBar } from './ChannelAccountBar';
import { AddWhatsAppAccountForm } from './AddWhatsAppAccountForm';
import { AddInstagramAccountForm } from './AddInstagramAccountForm';
import { AddTelegramAccountForm } from './AddTelegramAccountForm';
import { useChannelAccounts } from '../hooks/useChannelAccounts';
import {
  getInstagramChannelAccountSettings,
  getTelegramChannelAccountSettings,
  getWhatsAppChannelAccountSettings,
  getWhatsAppSubscribedApps,
  registerWhatsAppPhoneNumber,
  sendInstagramChannelAccountTestMessage,
  sendTelegramChannelAccountTestMessage,
  sendWhatsAppChannelAccountTestMessage,
  subscribeWhatsAppApp,
  unsubscribeWhatsAppApp,
  createTelegramChannelAccount,
  updateInstagramChannelAccountCredentials,
  updateTelegramChannelAccountCredentials,
  updateWhatsAppChannelAccountCredentials,
} from '../services/channelAccountService';
import {
  ChannelAccountInstagramSettings,
  ChannelAccountTelegramSettings,
  ChannelAccountWhatsAppSettings,
  WhatsAppPhoneNumberRegistration,
  WhatsAppSubscribedApps,
} from '../types/channelAccount';
import ConversationChannelBadge from '../../conversations/components/ConversationChannelBadge';
import { ConnectorTab, getConnectorTabChannelIcon } from '../utils/connectorTabIcons';
import styles from './ConnectorsPage.module.css';
import { ConnectorFieldStatus, ConnectorSubscribedFields } from './ConnectorFieldStatus';

const getDefaultInstagramOAuthFrontendReturnUrl = (): string =>
  `${window.location.origin.replace(/\/$/, '')}/admin/connectors?tab=instagram`;

const getDefaultInstagramOAuthRedirectUri = (webhookUrl?: string): string => {
  if (webhookUrl) {
    try {
      const parsed = new URL(webhookUrl);
      return `${parsed.origin}/api/v1/instagram/settings/oauth/callback`;
    } catch {
      // fall through
    }
  }

  const apiBase = (process.env.REACT_APP_API_BASE_URL || window.location.origin).replace(/\/$/, '');
  return `${apiBase}/api/v1/instagram/settings/oauth/callback`;
};

const emptyTelegramSettingsForm: UpdateTelegramSettingsRequest = {
  botToken: '',
  webhookSecretToken: '',
  apiBaseUrl: 'https://api.telegram.org',
};

const emptyWhatsAppSettingsForm: UpdateWhatsAppSettingsRequest = {
  cloudApiBaseUrl: 'https://graph.facebook.com',
  cloudApiVersion: 'v25.0',
  whatsAppAccountId: '',
  cloudPhoneNumberId: '',
  phoneNumber: '',
  cloudAccessToken: '',
  cloudWebhookVerifyToken: '',
  cloudAppSecret: '',
  validateCloudSignature: false,
};

const emptyInstagramSettingsForm: UpdateInstagramSettingsRequest = {
  apiBaseUrl: 'https://graph.instagram.com',
  apiVersion: 'v25.0',
  instagramBusinessAccountId: '',
  pageAccessToken: '',
  webhookVerifyToken: '',
  appId: '',
  appSecret: '',
  oauthRedirectUri: '',
  oauthFrontendReturnUrl: getDefaultInstagramOAuthFrontendReturnUrl(),
  validateSignature: false,
  defaultCommentReplyMode: 'public_comment' as const,
};

const parseConnectorTab = (value: string | null): ConnectorTab | null => {
  if (value === 'telegram'
    || value === 'whatsapp'
    || value === 'instagram'
    || value === 'messenger'
    || value === 'tiktok'
    || value === 'webchat'
    || value === 'calls'
    || value === 'sms') {
    return value;
  }

  return null;
};

const formatDateTime = (value?: string): string => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

const parseWebhookValidationErrorKey = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return 'telegramWebhookUrlRequired';
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return 'telegramWebhookUrlInvalid';
  }

  if (parsed.protocol !== 'https:') {
    return 'telegramWebhookUrlHttpsRequired';
  }

  const host = parsed.hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
    return 'telegramWebhookUrlPublicRequired';
  }

  return null;
};

const parseTestSendValidationErrorKey = (chatId: string, text: string): string | null => {
  if (!chatId.trim()) {
    return 'telegramTestChatIdRequired';
  }

  if (!text.trim()) {
    return 'telegramTestMessageRequired';
  }

  return null;
};

export const ConnectorsPage: React.FC = () => {
  const { t } = useTranslation(['connectors', 'companies', 'admin']);
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canManageConnectors = hasPermission(PermissionScopes.connectorsManage);

  const connectorTabs = React.useMemo<Array<{ id: ConnectorTab; label: string }>>(() => ([
    { id: 'telegram', label: 'telegramTab' },
    { id: 'whatsapp', label: 'whatsappTab' },
    { id: 'instagram', label: 'instagramTab' },
    { id: 'messenger', label: 'messengerTab' },
    { id: 'tiktok', label: 'tiktokTab' },
    { id: 'webchat', label: 'webchatTab' },
    { id: 'calls', label: 'callsTab' },
    { id: 'sms', label: 'smsTab' },
  ]), []);

  const [activeTab, setActiveTab] = React.useState<ConnectorTab>('telegram');

  const [loadingTelegramSettings, setLoadingTelegramSettings] = React.useState(false);
  const [savingTelegramSettings, setSavingTelegramSettings] = React.useState(false);
  const [telegramAccountSettings, setTelegramAccountSettings] = React.useState<ChannelAccountTelegramSettings | null>(null);
  const [telegramSettingsForm, setTelegramSettingsForm] = React.useState<UpdateTelegramSettingsRequest>(emptyTelegramSettingsForm);
  const [telegramSettingsError, setTelegramSettingsError] = React.useState<string | null>(null);
  const [telegramSettingsNotice, setTelegramSettingsNotice] = React.useState<string | null>(null);
  const [loadingTelegramWebhookInfo, setLoadingTelegramWebhookInfo] = React.useState(false);
  const [telegramWebhookInfo, setTelegramWebhookInfo] = React.useState<TelegramWebhookInfo | null>(null);
  const [registeringTelegramWebhook, setRegisteringTelegramWebhook] = React.useState(false);
  const [testingTelegramSend, setTestingTelegramSend] = React.useState(false);
  const [latestTelegramTestSendResult, setLatestTelegramTestSendResult] = React.useState<TelegramTestSendResult | null>(null);
  const [telegramWebhookUrl, setTelegramWebhookUrl] = React.useState<string>('');
  const [telegramTestChatId, setTelegramTestChatId] = React.useState<string>('');
  const [telegramTestMessage, setTelegramTestMessage] = React.useState<string>('Hello from ShahAgent Telegram test');
  const [loadingWhatsAppSettings, setLoadingWhatsAppSettings] = React.useState(false);
  const [savingWhatsAppSettings, setSavingWhatsAppSettings] = React.useState(false);
  const [whatsappAccountSettings, setWhatsappAccountSettings] = React.useState<ChannelAccountWhatsAppSettings | null>(null);
  const [whatsappSettingsForm, setWhatsappSettingsForm] = React.useState<UpdateWhatsAppSettingsRequest>(emptyWhatsAppSettingsForm);
  const [whatsappSettingsError, setWhatsappSettingsError] = React.useState<string | null>(null);
  const [whatsappSettingsNotice, setWhatsappSettingsNotice] = React.useState<string | null>(null);
  const [testingWhatsAppSend, setTestingWhatsAppSend] = React.useState(false);
  const [latestWhatsAppTestSendResult, setLatestWhatsAppTestSendResult] = React.useState<WhatsAppTestSendResult | null>(null);
  const [whatsappWebhookUrl, setWhatsappWebhookUrl] = React.useState<string>('');
  const [whatsappTestPhoneNumber, setWhatsappTestPhoneNumber] = React.useState<string>('');
  const [whatsappTestMessage, setWhatsappTestMessage] = React.useState<string>('Hello from ShahAgent WhatsApp test');
  const [whatsappTestPreviewUrl, setWhatsappTestPreviewUrl] = React.useState(true);
  const [whatsappRegisterPin, setWhatsappRegisterPin] = React.useState<string>('');
  const [registeringWhatsAppNumber, setRegisteringWhatsAppNumber] = React.useState(false);
  const [latestWhatsAppRegistration, setLatestWhatsAppRegistration] = React.useState<WhatsAppPhoneNumberRegistration | null>(null);
  const [subscribingWhatsAppApp, setSubscribingWhatsAppApp] = React.useState(false);
  const [unsubscribingWhatsAppApp, setUnsubscribingWhatsAppApp] = React.useState(false);
  const [loadingWhatsAppSubscribedApps, setLoadingWhatsAppSubscribedApps] = React.useState(false);
  const [whatsappSubscribedApps, setWhatsappSubscribedApps] = React.useState<WhatsAppSubscribedApps | null>(null);
  const [loadingInstagramSettings, setLoadingInstagramSettings] = React.useState(false);
  const [savingInstagramSettings, setSavingInstagramSettings] = React.useState(false);
  const [instagramTenantSettings, setInstagramTenantSettings] = React.useState<InstagramSettings | null>(null);
  const [instagramAccountSettings, setInstagramAccountSettings] = React.useState<ChannelAccountInstagramSettings | null>(null);
  const [instagramSettingsForm, setInstagramSettingsForm] = React.useState<UpdateInstagramSettingsRequest>(emptyInstagramSettingsForm);
  const [instagramSettingsError, setInstagramSettingsError] = React.useState<string | null>(null);
  const [instagramSettingsNotice, setInstagramSettingsNotice] = React.useState<string | null>(null);
  const [testingInstagramSend, setTestingInstagramSend] = React.useState(false);
  const [subscribingInstagramWebhooks, setSubscribingInstagramWebhooks] = React.useState(false);
  const [unsubscribingInstagramWebhooks, setUnsubscribingInstagramWebhooks] = React.useState(false);
  const [loadingInstagramWebhookSubscriptions, setLoadingInstagramWebhookSubscriptions] = React.useState(false);
  const [latestInstagramTestSendResult, setLatestInstagramTestSendResult] = React.useState<InstagramTestSendResult | null>(null);
  const [latestInstagramSubscriptionResult, setLatestInstagramSubscriptionResult] = React.useState<InstagramWebhookSubscription | null>(null);
  const [instagramWebhookSubscriptionStatus, setInstagramWebhookSubscriptionStatus] = React.useState<InstagramWebhookSubscriptionStatus | null>(null);
  const [instagramWebhookUrl, setInstagramWebhookUrl] = React.useState<string>('');
  const [instagramTestRecipientId, setInstagramTestRecipientId] = React.useState<string>('');
  const [instagramTestCommentId, setInstagramTestCommentId] = React.useState<string>('');
  const [instagramTestMessage, setInstagramTestMessage] = React.useState<string>('Hello from ShahAgent Instagram test');
  const [connectingInstagramOAuth, setConnectingInstagramOAuth] = React.useState(false);
  const [showAddWhatsAppAccount, setShowAddWhatsAppAccount] = React.useState(false);
  const [showAddInstagramAccount, setShowAddInstagramAccount] = React.useState(false);
  const [showAddTelegramAccount, setShowAddTelegramAccount] = React.useState(false);

  const whatsappChannelAccounts = useChannelAccounts('WhatsApp', canManageConnectors && activeTab === 'whatsapp');
  const instagramChannelAccounts = useChannelAccounts('Instagram', canManageConnectors && activeTab === 'instagram');
  const telegramChannelAccounts = useChannelAccounts('Telegram', canManageConnectors && activeTab === 'telegram');
  const messengerChannelAccounts = useChannelAccounts('Messenger', canManageConnectors && activeTab === 'messenger');
  const tiktokChannelAccounts = useChannelAccounts('TikTok', canManageConnectors && activeTab === 'tiktok');
  const voiceChannelAccounts = useChannelAccounts('Voice', canManageConnectors && activeTab === 'calls');
  const smsChannelAccounts = useChannelAccounts('Sms', canManageConnectors && activeTab === 'sms');
  const hasTelegramSettingsChanges = React.useMemo(() => {
    if (!telegramAccountSettings) {
      return Boolean(telegramSettingsForm.botToken?.trim())
        || Boolean(telegramSettingsForm.webhookSecretToken?.trim())
        || (telegramSettingsForm.apiBaseUrl?.trim() || 'https://api.telegram.org') !== 'https://api.telegram.org';
    }

    return Boolean(telegramSettingsForm.botToken?.trim())
      || Boolean(telegramSettingsForm.webhookSecretToken?.trim())
      || (telegramSettingsForm.apiBaseUrl?.trim() || 'https://api.telegram.org') !== (telegramAccountSettings.apiBaseUrl || 'https://api.telegram.org');
  }, [telegramAccountSettings, telegramSettingsForm]);

  const hasWhatsAppSettingsChanges = React.useMemo(() => {
    if (!whatsappAccountSettings) {
      return Boolean(whatsappSettingsForm.cloudAccessToken?.trim())
        || Boolean(whatsappSettingsForm.cloudWebhookVerifyToken?.trim())
        || Boolean(whatsappSettingsForm.cloudAppSecret?.trim())
        || Boolean(whatsappSettingsForm.cloudPhoneNumberId?.trim())
        || Boolean(whatsappSettingsForm.whatsAppAccountId?.trim())
        || Boolean(whatsappSettingsForm.phoneNumber?.trim())
        || (whatsappSettingsForm.cloudApiBaseUrl?.trim() || 'https://graph.facebook.com') !== 'https://graph.facebook.com'
        || (whatsappSettingsForm.cloudApiVersion?.trim() || 'v25.0') !== 'v25.0'
        || Boolean(whatsappSettingsForm.validateCloudSignature);
    }

    return Boolean(whatsappSettingsForm.cloudAccessToken?.trim())
      || Boolean(whatsappSettingsForm.cloudWebhookVerifyToken?.trim())
      || Boolean(whatsappSettingsForm.cloudAppSecret?.trim())
      || (whatsappSettingsForm.cloudPhoneNumberId?.trim() || '') !== (whatsappAccountSettings.cloudPhoneNumberId || '')
      || (whatsappSettingsForm.whatsAppAccountId?.trim() || '') !== (whatsappAccountSettings.whatsAppAccountId || '')
      || (whatsappSettingsForm.phoneNumber?.trim() || '') !== (whatsappAccountSettings.phoneNumber || '')
      || (whatsappSettingsForm.cloudApiBaseUrl?.trim() || 'https://graph.facebook.com') !== (whatsappAccountSettings.cloudApiBaseUrl || 'https://graph.facebook.com')
      || (whatsappSettingsForm.cloudApiVersion?.trim() || 'v25.0') !== (whatsappAccountSettings.cloudApiVersion || 'v25.0')
      || Boolean(whatsappSettingsForm.validateCloudSignature) !== whatsappAccountSettings.validateCloudSignature;
  }, [whatsappAccountSettings, whatsappSettingsForm]);

  const hasInstagramSettingsChanges = React.useMemo(() => {
    const accountBaseline = instagramAccountSettings;
    const tenantBaseline = instagramTenantSettings;

    const accountChanged = !accountBaseline
      ? Boolean(instagramSettingsForm.pageAccessToken?.trim())
        || Boolean(instagramSettingsForm.instagramBusinessAccountId?.trim())
        || (instagramSettingsForm.apiBaseUrl?.trim() || 'https://graph.instagram.com') !== 'https://graph.instagram.com'
        || (instagramSettingsForm.apiVersion?.trim() || 'v25.0') !== 'v25.0'
        || (instagramSettingsForm.defaultCommentReplyMode ?? 'public_comment') !== 'public_comment'
      : Boolean(instagramSettingsForm.pageAccessToken?.trim())
        || (instagramSettingsForm.instagramBusinessAccountId?.trim() || '') !== (accountBaseline.instagramBusinessAccountId || '')
        || (instagramSettingsForm.apiBaseUrl?.trim() || 'https://graph.instagram.com') !== (accountBaseline.apiBaseUrl || 'https://graph.instagram.com')
        || (instagramSettingsForm.apiVersion?.trim() || 'v25.0') !== (accountBaseline.apiVersion || 'v25.0')
        || (instagramSettingsForm.defaultCommentReplyMode ?? 'public_comment') !== (accountBaseline.defaultCommentReplyMode ?? 'public_comment');

    const tenantChanged = !tenantBaseline
      ? Boolean(instagramSettingsForm.webhookVerifyToken?.trim())
        || Boolean(instagramSettingsForm.appId?.trim())
        || Boolean(instagramSettingsForm.appSecret?.trim())
        || Boolean(instagramSettingsForm.oauthRedirectUri?.trim())
        || Boolean(instagramSettingsForm.oauthFrontendReturnUrl?.trim())
        || Boolean(instagramSettingsForm.validateSignature)
      : Boolean(instagramSettingsForm.webhookVerifyToken?.trim())
        || Boolean(instagramSettingsForm.appId?.trim())
        || Boolean(instagramSettingsForm.appSecret?.trim())
        || (instagramSettingsForm.oauthRedirectUri?.trim() || '') !== (tenantBaseline.oauthRedirectUri || '')
        || (instagramSettingsForm.oauthFrontendReturnUrl?.trim() || getDefaultInstagramOAuthFrontendReturnUrl()) !== (tenantBaseline.oauthFrontendReturnUrl || getDefaultInstagramOAuthFrontendReturnUrl())
        || Boolean(instagramSettingsForm.validateSignature) !== tenantBaseline.validateSignature;

    return accountChanged || tenantChanged;
  }, [instagramAccountSettings, instagramTenantSettings, instagramSettingsForm]);
  const loadTelegramWebhookInfo = React.useCallback(async (hasBotToken: boolean, suppressErrors = false, channelAccountId?: string | null) => {
    if (!canManageConnectors || !hasBotToken) {
      setTelegramWebhookInfo(null);
      return;
    }

    setLoadingTelegramWebhookInfo(true);

    try {
      const data = await getTelegramWebhookInfo(channelAccountId ?? undefined);
      setTelegramWebhookInfo(data);
    } catch (error) {
      setTelegramWebhookInfo(null);
      if (!suppressErrors) {
        setTelegramSettingsError(error instanceof Error ? error.message : t('failedToLoadTelegramWebhookInfo', { ns: 'companies' }));
      }
    } finally {
      setLoadingTelegramWebhookInfo(false);
    }
  }, [canManageConnectors, t]);

  const loadTelegramAccountSettings = React.useCallback(async (accountId: string) => {
    if (!canManageConnectors) {
      return;
    }

    setLoadingTelegramSettings(true);
    setTelegramSettingsError(null);
    setTelegramSettingsNotice(null);

    try {
      const data = await getTelegramChannelAccountSettings(accountId);
      setTelegramAccountSettings(data);
      setLatestTelegramTestSendResult(null);
      setTelegramSettingsForm({
        botToken: '',
        webhookSecretToken: '',
        apiBaseUrl: data.apiBaseUrl || 'https://api.telegram.org',
      });
      const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
      const defaultWebhookUrl = apiBase
        ? `${apiBase}/api/v1/telegram/webhook`
        : `${window.location.origin.replace(/\/$/, '')}/api/v1/telegram/webhook`;
      setTelegramWebhookUrl(defaultWebhookUrl);
      await loadTelegramWebhookInfo(data.hasBotToken, true, accountId);
    } catch (error) {
      setTelegramSettingsError(error instanceof Error ? error.message : t('failedToLoadTelegramSettings', { ns: 'companies' }));
    } finally {
      setLoadingTelegramSettings(false);
    }
  }, [canManageConnectors, loadTelegramWebhookInfo, t]);

  const loadWhatsAppAccountSettings = React.useCallback(async (accountId: string) => {
    if (!canManageConnectors) {
      return;
    }

    setLoadingWhatsAppSettings(true);
    setWhatsappSettingsError(null);
    setWhatsappSettingsNotice(null);

    try {
      const data = await getWhatsAppChannelAccountSettings(accountId);
      setWhatsappAccountSettings(data);
      setLatestWhatsAppTestSendResult(null);
      setLatestWhatsAppRegistration(null);
      setWhatsappRegisterPin('');
      setWhatsappSubscribedApps(null);
      setWhatsappSettingsForm({
        cloudAccessToken: '',
        cloudWebhookVerifyToken: '',
        cloudAppSecret: '',
        cloudApiBaseUrl: data.cloudApiBaseUrl || 'https://graph.facebook.com',
        cloudApiVersion: data.cloudApiVersion || 'v25.0',
        whatsAppAccountId: data.whatsAppAccountId || '',
        cloudPhoneNumberId: data.cloudPhoneNumberId || '',
        phoneNumber: data.phoneNumber || '',
        validateCloudSignature: data.validateCloudSignature,
      });
      const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
      const defaultWebhookUrl = apiBase
        ? `${apiBase}/api/v1/whatsapp/cloud/webhook`
        : `${window.location.origin.replace(/\/$/, '')}/api/v1/whatsapp/cloud/webhook`;
      setWhatsappWebhookUrl(defaultWebhookUrl);

      if (data.hasCloudAccessToken && data.whatsAppAccountId) {
        setLoadingWhatsAppSubscribedApps(true);
        try {
          setWhatsappSubscribedApps(await getWhatsAppSubscribedApps(accountId));
        } catch {
          setWhatsappSubscribedApps(null);
        } finally {
          setLoadingWhatsAppSubscribedApps(false);
        }
      }
    } catch (error) {
      setWhatsappSettingsError(error instanceof Error ? error.message : t('failedToLoadWhatsAppSettings', { ns: 'companies' }));
    } finally {
      setLoadingWhatsAppSettings(false);
    }
  }, [canManageConnectors, t]);

  const loadInstagramTenantSettings = React.useCallback(async () => {
    if (!canManageConnectors) {
      return;
    }

    setInstagramSettingsError(null);
    setInstagramSettingsNotice(null);

    try {
      const data = await getInstagramSettings();
      setInstagramTenantSettings(data);
      const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
      const defaultWebhookUrl = apiBase
        ? `${apiBase}/api/v1/instagram/webhook`
        : `${window.location.origin.replace(/\/$/, '')}/api/v1/instagram/webhook`;
      setInstagramWebhookUrl(defaultWebhookUrl);
      setInstagramSettingsForm(prev => ({
        ...prev,
        webhookVerifyToken: '',
        appId: '',
        appSecret: '',
        oauthRedirectUri: data.oauthRedirectUri || data.suggestedOAuthRedirectUri || getDefaultInstagramOAuthRedirectUri(defaultWebhookUrl),
        oauthFrontendReturnUrl: data.oauthFrontendReturnUrl || data.suggestedOAuthFrontendReturnUrl || getDefaultInstagramOAuthFrontendReturnUrl(),
        validateSignature: data.validateSignature,
      }));
    } catch (error) {
      setInstagramSettingsError(error instanceof Error ? error.message : t('failedToLoadInstagramSettings', { ns: 'companies' }));
    }
  }, [canManageConnectors, t]);

  const loadInstagramAccountSettings = React.useCallback(async (accountId: string) => {
    if (!canManageConnectors) {
      return;
    }

    setLoadingInstagramSettings(true);
    setInstagramSettingsError(null);
    setInstagramSettingsNotice(null);

    try {
      const data = await getInstagramChannelAccountSettings(accountId);
      setInstagramAccountSettings(data);
      setLatestInstagramTestSendResult(null);
      setLatestInstagramSubscriptionResult(null);
      setInstagramWebhookSubscriptionStatus(null);
      setInstagramSettingsForm(prev => ({
        ...prev,
        pageAccessToken: '',
        apiBaseUrl: data.apiBaseUrl || 'https://graph.instagram.com',
        apiVersion: data.apiVersion || 'v25.0',
        instagramBusinessAccountId: data.instagramBusinessAccountId || '',
        defaultCommentReplyMode: (data.defaultCommentReplyMode ?? 'public_comment') as 'public_comment' | 'private_comment',
      }));

      if (data.hasPageAccessToken && data.instagramBusinessAccountId) {
        setLoadingInstagramWebhookSubscriptions(true);
        try {
          const subscriptionStatus = await getInstagramWebhookSubscriptions(accountId);
          setInstagramWebhookSubscriptionStatus(subscriptionStatus);
        } catch {
          setInstagramWebhookSubscriptionStatus(null);
        } finally {
          setLoadingInstagramWebhookSubscriptions(false);
        }
      }
    } catch (error) {
      setInstagramSettingsError(error instanceof Error ? error.message : t('failedToLoadInstagramSettings', { ns: 'companies' }));
    } finally {
      setLoadingInstagramSettings(false);
    }
  }, [canManageConnectors, t]);
  const saveTelegramSettings = async () => {
    const selectedAccount = telegramChannelAccounts.selectedAccount;
    const isInitialSetup = telegramChannelAccounts.accounts.length === 0;

    if (!selectedAccount && !isInitialSetup) {
      return;
    }

    if (isInitialSetup && !telegramSettingsForm.botToken?.trim()) {
      const message = t('telegramBotTokenRequired', { ns: 'companies', defaultValue: 'Telegram bot token is required.' });
      setTelegramSettingsError(message);
      showToast(message, 'error');
      return;
    }

    setSavingTelegramSettings(true);
    setTelegramSettingsError(null);
    setTelegramSettingsNotice(null);

    try {
      if (isInitialSetup) {
        await createTelegramChannelAccount({
          displayName: 'Telegram',
          botToken: telegramSettingsForm.botToken?.trim() || '',
          webhookSecretToken: telegramSettingsForm.webhookSecretToken?.trim() || undefined,
          apiBaseUrl: telegramSettingsForm.apiBaseUrl?.trim() || undefined,
          isDefault: true,
        });
        await telegramChannelAccounts.reload();
      } else if (selectedAccount) {
        await updateTelegramChannelAccountCredentials(selectedAccount.id, {
          botToken: telegramSettingsForm.botToken?.trim() || undefined,
          webhookSecretToken: telegramSettingsForm.webhookSecretToken?.trim() || undefined,
          apiBaseUrl: telegramSettingsForm.apiBaseUrl?.trim() || undefined,
        });
        await loadTelegramAccountSettings(selectedAccount.id);
        await telegramChannelAccounts.reload();
      }

      setLatestTelegramTestSendResult(null);
      setTelegramSettingsForm(prev => ({
        ...prev,
        botToken: '',
        webhookSecretToken: '',
      }));
      setTelegramSettingsNotice(t('telegramSettingsSaved', { ns: 'companies' }));
      showToast(t('telegramSettingsSaved', { ns: 'companies' }), 'success');
    } catch (error) {
      setTelegramSettingsError(error instanceof Error ? error.message : t('failedToSaveTelegramSettings', { ns: 'companies' }));
      showToast(t('failedToSaveTelegramSettings', { ns: 'companies' }), 'error');
    } finally {
      setSavingTelegramSettings(false);
    }
  };

  const saveWhatsAppSettings = async () => {
    const selectedAccount = whatsappChannelAccounts.selectedAccount;
    if (!selectedAccount) {
      return;
    }

    setSavingWhatsAppSettings(true);
    setWhatsappSettingsError(null);
    setWhatsappSettingsNotice(null);

    try {
      await updateWhatsAppChannelAccountCredentials(selectedAccount.id, {
        cloudApiBaseUrl: whatsappSettingsForm.cloudApiBaseUrl?.trim() || undefined,
        cloudApiVersion: whatsappSettingsForm.cloudApiVersion?.trim() || undefined,
        whatsAppAccountId: whatsappSettingsForm.whatsAppAccountId?.trim() || undefined,
        cloudPhoneNumberId: whatsappSettingsForm.cloudPhoneNumberId?.trim() || undefined,
        phoneNumber: whatsappSettingsForm.phoneNumber?.trim() || undefined,
        cloudAccessToken: whatsappSettingsForm.cloudAccessToken?.trim() || undefined,
        cloudWebhookVerifyToken: whatsappSettingsForm.cloudWebhookVerifyToken?.trim() || undefined,
        cloudAppSecret: whatsappSettingsForm.cloudAppSecret?.trim() || undefined,
        validateCloudSignature: whatsappSettingsForm.validateCloudSignature,
      });

      await loadWhatsAppAccountSettings(selectedAccount.id);
      await whatsappChannelAccounts.reload();
      setWhatsappSettingsNotice(t('whatsappSettingsSaved', { ns: 'companies' }));
      showToast(t('whatsappSettingsSaved', { ns: 'companies' }), 'success');
    } catch (error) {
      setWhatsappSettingsError(error instanceof Error ? error.message : t('failedToSaveWhatsAppSettings', { ns: 'companies' }));
      showToast(t('failedToSaveWhatsAppSettings', { ns: 'companies' }), 'error');
    } finally {
      setSavingWhatsAppSettings(false);
    }
  };

  const sendWhatsAppTestMessageAction = async () => {
    const selectedAccount = whatsappChannelAccounts.selectedAccount;
    if (!selectedAccount) {
      return;
    }

    if (!whatsappTestPhoneNumber.trim()) {
      const message = t('whatsappTestPhoneRequired', { ns: 'companies' });
      setWhatsappSettingsError(message);
      setWhatsappSettingsNotice(null);
      showToast(message, 'error');
      return;
    }

    if (!whatsappTestMessage.trim()) {
      const message = t('whatsappTestMessageRequired', { ns: 'companies' });
      setWhatsappSettingsError(message);
      setWhatsappSettingsNotice(null);
      showToast(message, 'error');
      return;
    }

    setTestingWhatsAppSend(true);
    setWhatsappSettingsError(null);
    setWhatsappSettingsNotice(null);

    try {
      const result = await sendWhatsAppChannelAccountTestMessage(selectedAccount.id, {
        to: whatsappTestPhoneNumber.trim(),
        body: whatsappTestMessage.trim(),
        type: 'text',
        previewUrl: whatsappTestPreviewUrl,
      });
      setLatestWhatsAppTestSendResult(result);
      setWhatsappSettingsNotice(t('whatsappTestSendSucceeded', {
        ns: 'companies',
        phoneNumber: result.to,
      }));
      showToast(t('whatsappTestSendSucceededToast', {
        ns: 'companies',
        phoneNumber: result.to,
        messageSid: result.messageSid,
        sentAt: formatDateTime(result.sentAt),
      }), 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToSendWhatsAppTestMessage', { ns: 'companies' });
      setWhatsappSettingsError(message);
      showToast(message, 'error');
    } finally {
      setTestingWhatsAppSend(false);
    }
  };

  const refreshWhatsAppSubscribedApps = React.useCallback(async (accountId: string) => {
    setLoadingWhatsAppSubscribedApps(true);
    try {
      setWhatsappSubscribedApps(await getWhatsAppSubscribedApps(accountId));
    } catch (error) {
      setWhatsappSubscribedApps(null);
      throw error;
    } finally {
      setLoadingWhatsAppSubscribedApps(false);
    }
  }, []);

  const registerWhatsAppNumberAction = async () => {
    const selectedAccount = whatsappChannelAccounts.selectedAccount;
    if (!selectedAccount) {
      return;
    }

    const pin = whatsappRegisterPin.trim();
    if (!/^\d{6}$/.test(pin)) {
      const message = t('whatsappRegisterPinRequired', { ns: 'companies' });
      setWhatsappSettingsError(message);
      setWhatsappSettingsNotice(null);
      showToast(message, 'error');
      return;
    }

    setRegisteringWhatsAppNumber(true);
    setWhatsappSettingsError(null);
    setWhatsappSettingsNotice(null);

    try {
      const result = await registerWhatsAppPhoneNumber(selectedAccount.id, { pin });
      setLatestWhatsAppRegistration(result);
      setWhatsappRegisterPin('');
      setWhatsappSettingsNotice(t('whatsappNumberRegistered', {
        ns: 'companies',
        cloudPhoneNumberId: result.cloudPhoneNumberId,
      }));
      showToast(t('whatsappNumberRegistered', {
        ns: 'companies',
        cloudPhoneNumberId: result.cloudPhoneNumberId,
      }), 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToRegisterWhatsAppNumber', { ns: 'companies' });
      setWhatsappSettingsError(message);
      showToast(message, 'error');
    } finally {
      setRegisteringWhatsAppNumber(false);
    }
  };

  const subscribeWhatsAppAppAction = async () => {
    const selectedAccount = whatsappChannelAccounts.selectedAccount;
    if (!selectedAccount) {
      return;
    }

    setSubscribingWhatsAppApp(true);
    setWhatsappSettingsError(null);
    setWhatsappSettingsNotice(null);

    try {
      const result = await subscribeWhatsAppApp(selectedAccount.id);
      setWhatsappSettingsNotice(t('whatsappAppSubscribed', {
        ns: 'companies',
        whatsAppAccountId: result.whatsAppAccountId,
      }));
      showToast(t('whatsappAppSubscribed', {
        ns: 'companies',
        whatsAppAccountId: result.whatsAppAccountId,
      }), 'success');
      await refreshWhatsAppSubscribedApps(selectedAccount.id);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToSubscribeWhatsAppApp', { ns: 'companies' });
      setWhatsappSettingsError(message);
      showToast(message, 'error');
    } finally {
      setSubscribingWhatsAppApp(false);
    }
  };

  const unsubscribeWhatsAppAppAction = async () => {
    const selectedAccount = whatsappChannelAccounts.selectedAccount;
    if (!selectedAccount) {
      return;
    }

    setUnsubscribingWhatsAppApp(true);
    setWhatsappSettingsError(null);
    setWhatsappSettingsNotice(null);

    try {
      await unsubscribeWhatsAppApp(selectedAccount.id);
      setWhatsappSettingsNotice(t('whatsappAppUnsubscribed', { ns: 'companies' }));
      showToast(t('whatsappAppUnsubscribed', { ns: 'companies' }), 'success');
      await refreshWhatsAppSubscribedApps(selectedAccount.id);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToUnsubscribeWhatsAppApp', { ns: 'companies' });
      setWhatsappSettingsError(message);
      showToast(message, 'error');
    } finally {
      setUnsubscribingWhatsAppApp(false);
    }
  };

  const refreshWhatsAppSubscribedAppsAction = async () => {
    const selectedAccount = whatsappChannelAccounts.selectedAccount;
    if (!selectedAccount) {
      return;
    }

    setWhatsappSettingsError(null);

    try {
      await refreshWhatsAppSubscribedApps(selectedAccount.id);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToLoadWhatsAppSubscribedApps', { ns: 'companies' });
      setWhatsappSettingsError(message);
      showToast(message, 'error');
    }
  };

  const saveInstagramSettings = async () => {
    const selectedAccount = instagramChannelAccounts.selectedAccount;
    if (!selectedAccount) {
      return;
    }

    setSavingInstagramSettings(true);
    setInstagramSettingsError(null);
    setInstagramSettingsNotice(null);

    try {
      await updateInstagramChannelAccountCredentials(selectedAccount.id, {
        apiBaseUrl: instagramSettingsForm.apiBaseUrl?.trim() || undefined,
        apiVersion: instagramSettingsForm.apiVersion?.trim() || undefined,
        instagramBusinessAccountId: instagramSettingsForm.instagramBusinessAccountId?.trim() || undefined,
        pageAccessToken: instagramSettingsForm.pageAccessToken?.trim() || undefined,
        defaultCommentReplyMode: instagramSettingsForm.defaultCommentReplyMode,
      });

      const updated = await updateInstagramSettings({
        webhookVerifyToken: instagramSettingsForm.webhookVerifyToken?.trim() || undefined,
        appId: instagramSettingsForm.appId?.trim() || undefined,
        appSecret: instagramSettingsForm.appSecret?.trim() || undefined,
        oauthRedirectUri: instagramSettingsForm.oauthRedirectUri?.trim() || undefined,
        oauthFrontendReturnUrl: instagramSettingsForm.oauthFrontendReturnUrl?.trim() || undefined,
        validateSignature: instagramSettingsForm.validateSignature,
      });

      setInstagramTenantSettings(updated);
      await loadInstagramAccountSettings(selectedAccount.id);
      await loadInstagramTenantSettings();
      await instagramChannelAccounts.reload();
      setInstagramSettingsNotice(t('instagramSettingsSaved', { ns: 'companies' }));
      showToast(t('instagramSettingsSaved', { ns: 'companies' }), 'success');
    } catch (error) {
      setInstagramSettingsError(error instanceof Error ? error.message : t('failedToSaveInstagramSettings', { ns: 'companies' }));
      showToast(t('failedToSaveInstagramSettings', { ns: 'companies' }), 'error');
    } finally {
      setSavingInstagramSettings(false);
    }
  };

  const subscribeInstagramWebhooksAction = async () => {
    const selectedAccount = instagramChannelAccounts.selectedAccount;
    if (!selectedAccount) {
      return;
    }

    setSubscribingInstagramWebhooks(true);
    setInstagramSettingsError(null);
    setInstagramSettingsNotice(null);

    try {
      const result = await subscribeInstagramWebhooks({ channelAccountId: selectedAccount.id });
      setLatestInstagramSubscriptionResult(result);
      setInstagramWebhookSubscriptionStatus({
        instagramBusinessAccountId: result.instagramBusinessAccountId,
        subscribedFields: result.subscribedFields.split(',').map(field => field.trim()).filter(Boolean),
      });
      setInstagramSettingsNotice(t('instagramWebhooksSubscribed', {
        ns: 'companies',
        instagramBusinessAccountId: result.instagramBusinessAccountId,
      }));
      showToast(t('instagramWebhooksSubscribedToast', {
        ns: 'companies',
        instagramBusinessAccountId: result.instagramBusinessAccountId,
        subscribedFields: result.subscribedFields,
        subscribedAt: formatDateTime(result.subscribedAtUtc),
      }), 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToSubscribeInstagramWebhooks', { ns: 'companies' });
      setInstagramSettingsError(message);
      showToast(message, 'error');
    } finally {
      setSubscribingInstagramWebhooks(false);
    }
  };

  const unsubscribeInstagramWebhooksAction = async () => {
    const selectedAccount = instagramChannelAccounts.selectedAccount;
    if (!selectedAccount) {
      return;
    }

    setUnsubscribingInstagramWebhooks(true);
    setInstagramSettingsError(null);
    setInstagramSettingsNotice(null);

    try {
      await unsubscribeInstagramWebhooks(selectedAccount.id);
      setLatestInstagramSubscriptionResult(null);
      setInstagramWebhookSubscriptionStatus(null);
      setInstagramSettingsNotice(t('instagramWebhooksUnsubscribed', { ns: 'companies' }));
      showToast(t('instagramWebhooksUnsubscribedToast', { ns: 'companies' }), 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToUnsubscribeInstagramWebhooks', { ns: 'companies' });
      setInstagramSettingsError(message);
      showToast(message, 'error');
    } finally {
      setUnsubscribingInstagramWebhooks(false);
    }
  };

  const sendInstagramTestMessageAction = async () => {
    const selectedAccount = instagramChannelAccounts.selectedAccount;
    if (!selectedAccount) {
      return;
    }

    if (!instagramTestRecipientId.trim() && !instagramTestCommentId.trim()) {
      const message = t('instagramTestTargetRequired', { ns: 'companies' });
      setInstagramSettingsError(message);
      setInstagramSettingsNotice(null);
      showToast(message, 'error');
      return;
    }

    if (!instagramTestMessage.trim()) {
      const message = t('instagramTestMessageRequired', { ns: 'companies' });
      setInstagramSettingsError(message);
      setInstagramSettingsNotice(null);
      showToast(message, 'error');
      return;
    }

    setTestingInstagramSend(true);
    setInstagramSettingsError(null);
    setInstagramSettingsNotice(null);

    try {
      const result = await sendInstagramChannelAccountTestMessage(selectedAccount.id, {
        recipientId: instagramTestRecipientId.trim() || undefined,
        commentId: instagramTestCommentId.trim() || undefined,
        text: instagramTestMessage.trim(),
      });
      setLatestInstagramTestSendResult(result);
      setInstagramSettingsNotice(t('instagramTestSendSucceeded', {
        ns: 'companies',
        recipientId: result.recipientId,
      }));
      showToast(t('instagramTestSendSucceededToast', {
        ns: 'companies',
        recipientId: result.recipientId,
        messageId: result.messageId,
        sentAt: formatDateTime(result.sentAtUtc),
      }), 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToSendInstagramTestMessage', { ns: 'companies' });
      setInstagramSettingsError(message);
      showToast(message, 'error');
    } finally {
      setTestingInstagramSend(false);
    }
  };
  const registerTelegramWebhookAction = async () => {
    const webhookValidationErrorKey = parseWebhookValidationErrorKey(telegramWebhookUrl);
    if (webhookValidationErrorKey) {
      const message = t(webhookValidationErrorKey, { ns: 'companies' });
      setTelegramSettingsError(message);
      setTelegramSettingsNotice(null);
      showToast(message, 'error');
      return;
    }

    setRegisteringTelegramWebhook(true);
    setTelegramSettingsError(null);
    setTelegramSettingsNotice(null);

    try {
      const result = await registerTelegramWebhook(
        { webhookUrl: telegramWebhookUrl.trim() },
        telegramChannelAccounts.selectedAccountId ?? undefined,
      );
      await loadTelegramWebhookInfo(true, true, telegramChannelAccounts.selectedAccountId);
      setTelegramSettingsNotice(t('telegramWebhookRegistered', {
        ns: 'companies',
        webhookUrl: result.webhookUrl,
      }));
      showToast(t('telegramWebhookRegisteredToast', {
        ns: 'companies',
        webhookUrl: result.webhookUrl,
        registeredAt: formatDateTime(result.registeredAt),
        secretToken: result.hasSecretToken ? t('configured', { ns: 'companies' }) : t('missing', { ns: 'companies' }),
      }), 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToRegisterTelegramWebhook', { ns: 'companies' });
      setTelegramSettingsError(message);
      showToast(message, 'error');
    } finally {
      setRegisteringTelegramWebhook(false);
    }
  };

  const sendTelegramTestMessageAction = async () => {
    const selectedAccount = telegramChannelAccounts.selectedAccount;
    if (!selectedAccount) {
      return;
    }

    const testSendValidationErrorKey = parseTestSendValidationErrorKey(telegramTestChatId, telegramTestMessage);
    if (testSendValidationErrorKey) {
      const message = t(testSendValidationErrorKey, { ns: 'companies' });
      setTelegramSettingsError(message);
      setTelegramSettingsNotice(null);
      showToast(message, 'error');
      return;
    }

    setTestingTelegramSend(true);
    setTelegramSettingsError(null);
    setTelegramSettingsNotice(null);

    try {
      const result = await sendTelegramChannelAccountTestMessage(selectedAccount.id, {
        chatId: telegramTestChatId.trim(),
        text: telegramTestMessage.trim(),
      });
      setLatestTelegramTestSendResult(result);
      setTelegramSettingsNotice(t('telegramTestSendSucceeded', {
        ns: 'companies',
        chatId: result.chatId,
      }));
      showToast(t('telegramTestSendSucceededToast', {
        ns: 'companies',
        chatId: result.chatId,
        messageId: result.messageId,
        sentAt: formatDateTime(result.sentAt),
      }), 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToSendTelegramTestMessage', { ns: 'companies' });
      setTelegramSettingsError(message);
      showToast(message, 'error');
    } finally {
      setTestingTelegramSend(false);
    }
  };

  const resolveInstagramOAuthRedirectUri = React.useCallback((): string => (
    instagramSettingsForm.oauthRedirectUri?.trim()
    || instagramTenantSettings?.oauthRedirectUri
    || instagramTenantSettings?.suggestedOAuthRedirectUri
    || getDefaultInstagramOAuthRedirectUri(instagramWebhookUrl)
  ), [instagramTenantSettings, instagramSettingsForm.oauthRedirectUri, instagramWebhookUrl]);

  const connectInstagramOAuth = async () => {
    const hasAppId = Boolean(instagramSettingsForm.appId?.trim() || instagramTenantSettings?.hasAppId);
    const hasAppSecret = Boolean(instagramSettingsForm.appSecret?.trim() || instagramTenantSettings?.hasAppSecret);
    const oauthRedirectUri = resolveInstagramOAuthRedirectUri();

    if (!hasAppId || !hasAppSecret || !oauthRedirectUri) {
      const message = t('instagramOAuthSaveFirst', { ns: 'companies' });
      setInstagramSettingsError(message);
      showToast(message, 'error');
      return;
    }

    setConnectingInstagramOAuth(true);
    setInstagramSettingsError(null);
    setInstagramSettingsNotice(null);

    try {
      const shouldPersistOAuthSettings = Boolean(instagramSettingsForm.appId?.trim())
        || Boolean(instagramSettingsForm.appSecret?.trim())
        || instagramSettingsForm.oauthRedirectUri?.trim() !== (instagramTenantSettings?.oauthRedirectUri || '');

      if (shouldPersistOAuthSettings || !instagramTenantSettings?.oauthRedirectUri) {
        await updateInstagramSettings({
          appId: instagramSettingsForm.appId?.trim() || undefined,
          appSecret: instagramSettingsForm.appSecret?.trim() || undefined,
          oauthRedirectUri,
          oauthFrontendReturnUrl: instagramSettingsForm.oauthFrontendReturnUrl?.trim()
            || instagramTenantSettings?.oauthFrontendReturnUrl
            || getDefaultInstagramOAuthFrontendReturnUrl(),
        });
        await loadInstagramTenantSettings();
      }

      const result = await startInstagramOAuth();
      if (!result.authorizeUrl) {
        throw new Error(t('instagramOAuthFailed', { ns: 'companies' }));
      }

      window.location.assign(result.authorizeUrl);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('instagramOAuthFailed', { ns: 'companies' });
      setInstagramSettingsError(message);
      showToast(message, 'error');
      setConnectingInstagramOAuth(false);
    }
  };

  const breadcrumbItems: BreadcrumbItem[] = React.useMemo(() => [
    { label: t('admin', { ns: 'admin' }), path: '/admin' },
    { label: t('connectors'), path: '/admin/connectors' },
  ], [t]);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = parseConnectorTab(params.get('tab'));
    if (tab) {
      setActiveTab(tab);
    }
  }, []);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthStatus = params.get('instagramOAuth');
    if (!oauthStatus) {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.delete('instagramOAuth');
    window.history.replaceState({}, '', url.toString());

    if (oauthStatus === 'success') {
      setActiveTab('instagram');
      showToast(t('instagramOAuthSuccess', { ns: 'companies' }), 'success');
      const accountId = instagramChannelAccounts.selectedAccountId;
      if (accountId) {
        void loadInstagramAccountSettings(accountId);
      }
      void loadInstagramTenantSettings();
      return;
    }

    setActiveTab('instagram');
    showToast(t('instagramOAuthFailed', { ns: 'companies' }), 'error');
  }, [instagramChannelAccounts.selectedAccountId, loadInstagramAccountSettings, loadInstagramTenantSettings, showToast, t]);

  React.useEffect(() => {
    if (!canManageConnectors || activeTab !== 'telegram') {
      return;
    }

    const accountId = telegramChannelAccounts.selectedAccountId;
    if (!accountId) {
      setTelegramAccountSettings(null);
      setTelegramSettingsForm(emptyTelegramSettingsForm);
      return;
    }

    void loadTelegramAccountSettings(accountId);
  }, [activeTab, canManageConnectors, loadTelegramAccountSettings, telegramChannelAccounts.selectedAccountId]);

  React.useEffect(() => {
    if (!canManageConnectors || activeTab !== 'whatsapp') {
      return;
    }

    const accountId = whatsappChannelAccounts.selectedAccountId;
    if (!accountId) {
      setWhatsappAccountSettings(null);
      setWhatsappSettingsForm(emptyWhatsAppSettingsForm);
      return;
    }

    void loadWhatsAppAccountSettings(accountId);
  }, [activeTab, canManageConnectors, loadWhatsAppAccountSettings, whatsappChannelAccounts.selectedAccountId]);

  React.useEffect(() => {
    if (!canManageConnectors || activeTab !== 'instagram') {
      return;
    }

    const accountId = instagramChannelAccounts.selectedAccountId;
    if (!accountId) {
      setInstagramAccountSettings(null);
      setInstagramSettingsForm(emptyInstagramSettingsForm);
      return;
    }

    void loadInstagramTenantSettings();
    void loadInstagramAccountSettings(accountId);
  }, [
    activeTab,
    canManageConnectors,
    instagramChannelAccounts.selectedAccountId,
    loadInstagramAccountSettings,
    loadInstagramTenantSettings,
  ]);

  const connectorTabReady = React.useMemo<Partial<Record<ConnectorTab, boolean>>>(() => ({
    telegram: telegramChannelAccounts.accounts.some(account => (
      account.credentialStatus?.hasAccessToken && account.credentialStatus?.hasWebhookVerifyToken
    )),
    whatsapp: whatsappChannelAccounts.accounts.some(account => (
      account.credentialStatus?.hasAccessToken && account.credentialStatus?.hasWebhookVerifyToken
    )),
    instagram: instagramChannelAccounts.accounts.some(account => (
      account.credentialStatus?.hasAccessToken
    )) && Boolean(instagramTenantSettings?.hasWebhookVerifyToken),
    messenger: messengerChannelAccounts.accounts.some(account => (
      account.credentialStatus?.hasAccessToken && account.credentialStatus?.hasWebhookVerifyToken
    )),
    tiktok: tiktokChannelAccounts.accounts.some(account => (
      account.credentialStatus?.hasAccessToken
    )),
    calls: voiceChannelAccounts.accounts.some(account => (
      account.credentialStatus?.hasAccessToken && account.credentialStatus?.hasWebhookVerifyToken
    )),
    // A gateway only exists once someone saved one, so the account itself is the signal.
    sms: smsChannelAccounts.accounts.length > 0,
  }), [telegramChannelAccounts.accounts, whatsappChannelAccounts.accounts, instagramChannelAccounts.accounts, messengerChannelAccounts.accounts, tiktokChannelAccounts.accounts, voiceChannelAccounts.accounts, smsChannelAccounts.accounts, instagramTenantSettings]);

  return (
    <div className="company-admin-page">
      <Breadcrumb items={breadcrumbItems} />
      <div className="company-page-head">
        <p className="company-page-head-description">
          {t('connectorsDescription')}
        </p>
        <div className={styles.connectorTabs} role="tablist" aria-label={t('connectorsManagementTabs')}>
          {connectorTabs.map(tab => {
            const tabLabel = t(tab.label, { ns: 'companies' });
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                data-channel={tab.id}
                className={`${styles.connectorTab} ${isActive ? styles.connectorTabActive : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <ConversationChannelBadge
                  icon={getConnectorTabChannelIcon(tab.id, tabLabel)}
                  size="compact"
                  disableTooltip
                />
                <span className={styles.connectorTabLabel}>{tabLabel}</span>
                {connectorTabReady[tab.id] ? (
                  <span
                    className={styles.connectorTabReadyCheck}
                    title={t('configured', { ns: 'companies' })}
                    aria-label={t('configured', { ns: 'companies' })}
                  >
                    ✓
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {!canManageConnectors ? (
        <div className="company-tab-readonly-hint" role="status">
          {t('connectorsTabReadOnlyHint', { ns: 'companies' })}
        </div>
      ) : null}

      <div className="company-tab-panel">
{activeTab === 'telegram' ? (
  <section className="company-section">
    <div className="company-section-head">
      <h3>{t('telegramSettingsHeading', { ns: 'companies' })}</h3>
    </div>

    <p style={{ color: '#516173', marginTop: 0 }}>
      {t('telegramSettingsDescription', { ns: 'companies' })}
    </p>

    {telegramSettingsError ? <div className="error">{telegramSettingsError}</div> : null}
    {telegramSettingsNotice ? <div className="success">{telegramSettingsNotice}</div> : null}

    <ChannelAccountBar
      channel="Telegram"
      accounts={telegramChannelAccounts.accounts}
      limits={telegramChannelAccounts.limits}
      loading={telegramChannelAccounts.loading}
      error={telegramChannelAccounts.error}
      selectedAccountId={telegramChannelAccounts.selectedAccountId}
      onSelectAccount={telegramChannelAccounts.setSelectedAccountId}
      onRefresh={telegramChannelAccounts.reload}
      canManage={canManageConnectors}
      showAddForm={showAddTelegramAccount}
      onToggleAddForm={() => setShowAddTelegramAccount(previous => !previous)}
      onAccountUpdated={() => {
        const accountId = telegramChannelAccounts.selectedAccountId;
        if (accountId) {
          void loadTelegramAccountSettings(accountId);
        }
      }}
    />

    {showAddTelegramAccount ? (
      <AddTelegramAccountForm
        canManage={canManageConnectors}
        onCancel={() => setShowAddTelegramAccount(false)}
        onCreated={() => {
          setShowAddTelegramAccount(false);
          void telegramChannelAccounts.reload();
          showToast(t('channelAccountCreated', { ns: 'companies' }), 'success');
        }}
      />
    ) : null}

    {telegramChannelAccounts.accounts.length === 0 ? (
      loadingTelegramSettings ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
          <Spinner text={t('loadingTelegramSettings', { ns: 'companies' })} />
        </div>
      ) : (
        <>
          <p style={{ color: '#516173', marginTop: 16 }}>
            {t('channelAccountCreateAccountForSettings', { ns: 'companies' })}
          </p>
          <div className="company-form-grid">
            <label>
              {t('telegramApiBaseUrl', { ns: 'companies' })}
              <input
                className="text-input"
                value={telegramSettingsForm.apiBaseUrl || ''}
                onChange={event => setTelegramSettingsForm(prev => ({ ...prev, apiBaseUrl: event.target.value }))}
                placeholder="https://api.telegram.org"
              />
            </label>
            <label>
              {t('telegramBotToken', { ns: 'companies' })}
              <input
                className="text-input"
                type="password"
                value={telegramSettingsForm.botToken || ''}
                onChange={event => setTelegramSettingsForm(prev => ({ ...prev, botToken: event.target.value }))}
                placeholder="123456:ABCDEF"
                autoComplete="new-password"
              />
            </label>
            <label>
              {t('telegramWebhookSecretToken', { ns: 'companies' })}
              <input
                className="text-input"
                type="password"
                value={telegramSettingsForm.webhookSecretToken || ''}
                onChange={event => setTelegramSettingsForm(prev => ({ ...prev, webhookSecretToken: event.target.value }))}
                placeholder={t('telegramWebhookSecretPlaceholder', { ns: 'companies' })}
                autoComplete="new-password"
              />
            </label>
          </div>
          <div className="company-actions-inline" style={{ marginTop: 16 }}>
            <button type="button" className="btn btn-primary" onClick={saveTelegramSettings} disabled={savingTelegramSettings || !hasTelegramSettingsChanges}>
              {savingTelegramSettings ? t('savingEllipsis', { ns: 'admin' }) : t('saveTelegramSettings', { ns: 'companies' })}
            </button>
          </div>
        </>
      )
    ) : telegramChannelAccounts.selectedAccountId ? (
      loadingTelegramSettings ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
          <Spinner text={t('loadingTelegramSettings', { ns: 'companies' })} />
        </div>
      ) : (
      <>
        <div className="company-form-grid">
          <label>
            {t('telegramApiBaseUrl', { ns: 'companies' })}
            <input
              className="text-input"
              value={telegramSettingsForm.apiBaseUrl || ''}
              onChange={event => setTelegramSettingsForm(prev => ({ ...prev, apiBaseUrl: event.target.value }))}
              placeholder="https://api.telegram.org"
            />
          </label>

          <label>
            {t('telegramBotToken', { ns: 'companies' })}
            <input
              className="text-input"
              type="password"
              value={telegramSettingsForm.botToken || ''}
              onChange={event => setTelegramSettingsForm(prev => ({ ...prev, botToken: event.target.value }))}
              placeholder={telegramAccountSettings?.hasBotToken ? t('telegramBotTokenConfigured', { ns: 'companies' }) : '123456:ABCDEF'}
              autoComplete="new-password"
            />
          </label>

          <label>
            {t('telegramWebhookSecretToken', { ns: 'companies' })}
            <input
              className="text-input"
              type="password"
              value={telegramSettingsForm.webhookSecretToken || ''}
              onChange={event => setTelegramSettingsForm(prev => ({ ...prev, webhookSecretToken: event.target.value }))}
              placeholder={telegramAccountSettings?.hasWebhookSecretToken ? t('telegramWebhookSecretConfigured', { ns: 'companies' }) : t('telegramWebhookSecretPlaceholder', { ns: 'companies' })}
              autoComplete="new-password"
            />
          </label>
        </div>

        <div style={{ display: 'grid', gap: 6, marginTop: 12, color: '#516173', fontSize: 13 }}>
          <div>{t('telegramBotTokenHint', { ns: 'companies' })}</div>
          <div>{t('telegramWebhookSecretHint', { ns: 'companies' })}</div>
          <div>{t('telegramSettingsStatus', {
            ns: 'companies',
            botToken: telegramAccountSettings?.hasBotToken ? t('configured', { ns: 'companies' }) : t('missing', { ns: 'companies' }),
            webhookSecret: telegramAccountSettings?.hasWebhookSecretToken ? t('configured', { ns: 'companies' }) : t('missing', { ns: 'companies' }),
          })}</div>
        </div>

        <div className="telegram-status-grid">
          <div className="telegram-status-card">
            <div className="telegram-status-card-title">{t('telegramStatusCredentials', { ns: 'companies' })}</div>
            <dl className="telegram-status-list">
              <div>
                <dt>{t('telegramStatusApiBaseUrl', { ns: 'companies' })}</dt>
                <dd>{telegramAccountSettings?.apiBaseUrl || telegramSettingsForm.apiBaseUrl || t('telegramStatusNotAvailable', { ns: 'companies' })}</dd>
              </div>
              <div>
                <dt>{t('telegramStatusBotToken', { ns: 'companies' })}</dt>
                <dd><ConnectorFieldStatus done={Boolean(telegramAccountSettings?.hasBotToken)} /></dd>
              </div>
              <div>
                <dt>{t('telegramStatusWebhookSecret', { ns: 'companies' })}</dt>
                <dd><ConnectorFieldStatus done={Boolean(telegramAccountSettings?.hasWebhookSecretToken)} /></dd>
              </div>
            </dl>
          </div>

          <div className="telegram-status-card">
            <div className="telegram-status-card-title">{t('telegramStatusWebhook', { ns: 'companies' })}</div>
            {loadingTelegramWebhookInfo ? (
              <div className="telegram-status-empty">{t('loadingTelegramWebhookInfo', { ns: 'companies' })}</div>
            ) : telegramWebhookInfo ? (
              <dl className="telegram-status-list">
                <div>
                  <dt>{t('telegramStatusWebhookUrl', { ns: 'companies' })}</dt>
                  <dd>{telegramWebhookInfo.webhookUrl || t('telegramStatusNotRegistered', { ns: 'companies' })}</dd>
                </div>
                <div>
                  <dt>{t('telegramStatusPendingUpdates', { ns: 'companies' })}</dt>
                  <dd>{telegramWebhookInfo.pendingUpdateCount}</dd>
                </div>
                <div>
                  <dt>{t('telegramStatusWebhookIpAddress', { ns: 'companies' })}</dt>
                  <dd>{telegramWebhookInfo.ipAddress || t('telegramStatusNotAvailable', { ns: 'companies' })}</dd>
                </div>
                <div>
                  <dt>{t('telegramStatusAllowedUpdates', { ns: 'companies' })}</dt>
                  <dd>{telegramWebhookInfo.allowedUpdates.length > 0 ? telegramWebhookInfo.allowedUpdates.join(', ') : t('telegramStatusAllUpdates', { ns: 'companies' })}</dd>
                </div>
                <div>
                  <dt>{t('telegramStatusLastError', { ns: 'companies' })}</dt>
                  <dd>{telegramWebhookInfo.lastErrorMessage
                    ? `${telegramWebhookInfo.lastErrorMessage}${telegramWebhookInfo.lastErrorAt ? ` (${formatDateTime(telegramWebhookInfo.lastErrorAt)})` : ''}`
                    : t('telegramStatusNoWebhookErrors', { ns: 'companies' })}</dd>
                </div>
              </dl>
            ) : (
              <div className="telegram-status-empty">{t('telegramStatusNoWebhookInfo', { ns: 'companies' })}</div>
            )}
          </div>

          <div className="telegram-status-card">
            <div className="telegram-status-card-title">{t('telegramStatusTestSend', { ns: 'companies' })}</div>
            {latestTelegramTestSendResult ? (
              <dl className="telegram-status-list">
                <div>
                  <dt>{t('telegramStatusChatId', { ns: 'companies' })}</dt>
                  <dd>{latestTelegramTestSendResult.chatId}</dd>
                </div>
                <div>
                  <dt>{t('telegramStatusMessageId', { ns: 'companies' })}</dt>
                  <dd>{latestTelegramTestSendResult.messageId}</dd>
                </div>
                <div>
                  <dt>{t('telegramStatusSentAt', { ns: 'companies' })}</dt>
                  <dd>{formatDateTime(latestTelegramTestSendResult.sentAt)}</dd>
                </div>
              </dl>
            ) : (
              <div className="telegram-status-empty">{t('telegramStatusNoTestSend', { ns: 'companies' })}</div>
            )}
          </div>
        </div>

        <div className="company-actions-inline" style={{ marginTop: 16 }}>
          <button type="button" className="btn btn-primary" onClick={saveTelegramSettings} disabled={savingTelegramSettings || !hasTelegramSettingsChanges}>
            {savingTelegramSettings ? t('savingEllipsis', { ns: 'admin' }) : t('saveTelegramSettings', { ns: 'companies' })}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setTelegramSettingsForm({
                botToken: '',
                webhookSecretToken: '',
                apiBaseUrl: telegramAccountSettings?.apiBaseUrl || 'https://api.telegram.org',
              });
              setTelegramSettingsError(null);
              setTelegramSettingsNotice(null);
            }}
            disabled={savingTelegramSettings}
          >
            {t('resetTelegramForm', { ns: 'companies' })}
          </button>
        </div>

        <div className="company-section" style={{ marginTop: 16 }}>
          <div className="company-section-head">
            <h3>{t('telegramWebhookSection', { ns: 'companies' })}</h3>
          </div>
          <div className="company-form-grid">
            <label>
              {t('telegramWebhookUrl', { ns: 'companies' })}
              <input
                className="text-input"
                value={telegramWebhookUrl}
                onChange={event => setTelegramWebhookUrl(event.target.value)}
                placeholder="https://api.example.com/api/v1/telegram/webhook"
              />
            </label>
          </div>
          <div style={{ color: '#516173', fontSize: 13, marginTop: 8 }}>
            {t('telegramWebhookRegistrationHint', { ns: 'companies' })}
          </div>
          <div className="company-actions-inline" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={registerTelegramWebhookAction}
              disabled={registeringTelegramWebhook || !telegramWebhookUrl.trim()}
            >
              {registeringTelegramWebhook ? t('savingEllipsis', { ns: 'admin' }) : t('registerTelegramWebhook', { ns: 'companies' })}
            </button>
          </div>
        </div>

        <div className="company-section" style={{ marginTop: 16 }}>
          <div className="company-section-head">
            <h3>{t('telegramTestSendSection', { ns: 'companies' })}</h3>
          </div>
          <div className="company-form-grid">
            <label>
              {t('telegramTestChatId', { ns: 'companies' })}
              <input
                className="text-input"
                value={telegramTestChatId}
                onChange={event => setTelegramTestChatId(event.target.value)}
                placeholder="932245001"
              />
            </label>
            <label>
              {t('telegramTestMessage', { ns: 'companies' })}
              <input
                className="text-input"
                value={telegramTestMessage}
                onChange={event => setTelegramTestMessage(event.target.value)}
              />
            </label>
          </div>
          <div style={{ color: '#516173', fontSize: 13, marginTop: 8 }}>
            {t('telegramTestSendHint', { ns: 'companies' })}
          </div>
          <div className="company-actions-inline" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={sendTelegramTestMessageAction}
              disabled={testingTelegramSend || !telegramTestChatId.trim() || !telegramTestMessage.trim()}
            >
              {testingTelegramSend ? t('savingEllipsis', { ns: 'admin' }) : t('sendTelegramTestMessage', { ns: 'companies' })}
            </button>
          </div>
        </div>
      </>
      )
    ) : null}
  </section>
) : null}

{activeTab === 'whatsapp' ? (
  <section className="company-section">
    <div className="company-section-head">
      <h3>{t('whatsappSettingsHeading', { ns: 'companies' })}</h3>
    </div>

    <p style={{ color: '#516173', marginTop: 0 }}>
      {t('whatsappSettingsDescription', { ns: 'companies' })}
    </p>

    {whatsappSettingsError ? <div className="error">{whatsappSettingsError}</div> : null}
    {whatsappSettingsNotice ? <div className="success">{whatsappSettingsNotice}</div> : null}

    <ChannelAccountBar
      channel="WhatsApp"
      accounts={whatsappChannelAccounts.accounts}
      limits={whatsappChannelAccounts.limits}
      loading={whatsappChannelAccounts.loading}
      error={whatsappChannelAccounts.error}
      selectedAccountId={whatsappChannelAccounts.selectedAccountId}
      onSelectAccount={whatsappChannelAccounts.setSelectedAccountId}
      onRefresh={whatsappChannelAccounts.reload}
      canManage={canManageConnectors}
      showAddForm={showAddWhatsAppAccount}
      onToggleAddForm={() => setShowAddWhatsAppAccount(previous => !previous)}
      onAccountUpdated={() => {
        const accountId = whatsappChannelAccounts.selectedAccountId;
        if (accountId) {
          void loadWhatsAppAccountSettings(accountId);
        }
      }}
    />

    {showAddWhatsAppAccount ? (
      <AddWhatsAppAccountForm
        canManage={canManageConnectors}
        onCancel={() => setShowAddWhatsAppAccount(false)}
        onCreated={() => {
          setShowAddWhatsAppAccount(false);
          void whatsappChannelAccounts.reload();
          showToast(t('channelAccountCreated', { ns: 'companies' }), 'success');
        }}
      />
    ) : null}

    {whatsappChannelAccounts.accounts.length === 0 ? (
      <p style={{ color: '#516173', marginTop: 16 }}>
        {t('channelAccountCreateAccountForSettings', { ns: 'companies' })}
      </p>
    ) : whatsappChannelAccounts.selectedAccountId ? (
      loadingWhatsAppSettings ? (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
        <Spinner text={t('loadingWhatsAppSettings', { ns: 'companies' })} />
      </div>
    ) : (
      <>
        <div className="company-form-grid">
          <label>
            {t('whatsappApiBaseUrl', { ns: 'companies' })}
            <input
              className="text-input"
              value={whatsappSettingsForm.cloudApiBaseUrl || ''}
              onChange={event => setWhatsappSettingsForm(prev => ({ ...prev, cloudApiBaseUrl: event.target.value }))}
              placeholder="https://graph.facebook.com"
            />
          </label>

          <label>
            {t('whatsappApiVersion', { ns: 'companies' })}
            <input
              className="text-input"
              value={whatsappSettingsForm.cloudApiVersion || ''}
              onChange={event => setWhatsappSettingsForm(prev => ({ ...prev, cloudApiVersion: event.target.value }))}
              placeholder="v25.0"
            />
          </label>

          <label>
            {t('whatsappAccountId', { ns: 'companies' })}
            <input
              className="text-input"
              value={whatsappSettingsForm.whatsAppAccountId || ''}
              onChange={event => setWhatsappSettingsForm(prev => ({ ...prev, whatsAppAccountId: event.target.value }))}
              placeholder="123456789012345"
            />
          </label>

          <label>
            {t('whatsappCloudPhoneNumberId', { ns: 'companies' })}
            <input
              className="text-input"
              value={whatsappSettingsForm.cloudPhoneNumberId || ''}
              onChange={event => setWhatsappSettingsForm(prev => ({ ...prev, cloudPhoneNumberId: event.target.value }))}
              placeholder="1068207776372985"
            />
          </label>

          <label>
            {t('whatsappPhoneNumber', { ns: 'companies' })}
            <input
              className="text-input"
              value={whatsappSettingsForm.phoneNumber || ''}
              onChange={event => setWhatsappSettingsForm(prev => ({ ...prev, phoneNumber: event.target.value }))}
              placeholder="+994501234567"
            />
          </label>

          <label>
            {t('whatsappAccessToken', { ns: 'companies' })}
            <input
              className="text-input"
              type="password"
              value={whatsappSettingsForm.cloudAccessToken || ''}
              onChange={event => setWhatsappSettingsForm(prev => ({ ...prev, cloudAccessToken: event.target.value }))}
              placeholder={whatsappAccountSettings?.hasCloudAccessToken ? t('whatsappAccessTokenConfigured', { ns: 'companies' }) : t('whatsappAccessTokenPlaceholder', { ns: 'companies' })}
              autoComplete="new-password"
            />
          </label>

          <label>
            {t('whatsappWebhookVerifyToken', { ns: 'companies' })}
            <input
              className="text-input"
              type="password"
              value={whatsappSettingsForm.cloudWebhookVerifyToken || ''}
              onChange={event => setWhatsappSettingsForm(prev => ({ ...prev, cloudWebhookVerifyToken: event.target.value }))}
              placeholder={whatsappAccountSettings?.hasCloudWebhookVerifyToken ? t('whatsappWebhookVerifyTokenConfigured', { ns: 'companies' }) : t('whatsappWebhookVerifyTokenPlaceholder', { ns: 'companies' })}
              autoComplete="new-password"
            />
          </label>

          <label>
            {t('whatsappAppSecret', { ns: 'companies' })}
            <input
              className="text-input"
              type="password"
              value={whatsappSettingsForm.cloudAppSecret || ''}
              onChange={event => setWhatsappSettingsForm(prev => ({ ...prev, cloudAppSecret: event.target.value }))}
              placeholder={whatsappAccountSettings?.hasCloudAppSecret ? t('whatsappAppSecretConfigured', { ns: 'companies' }) : t('whatsappAppSecretPlaceholder', { ns: 'companies' })}
              autoComplete="new-password"
            />
          </label>

          <ToggleField
            label={t('whatsappValidateCloudSignature', { ns: 'companies' })}
            checked={Boolean(whatsappSettingsForm.validateCloudSignature)}
            onChange={value => setWhatsappSettingsForm(prev => ({ ...prev, validateCloudSignature: value }))}
          />
        </div>

        <div style={{ display: 'grid', gap: 6, marginTop: 12, color: '#516173', fontSize: 13 }}>
          <div>{t('whatsappAccessTokenHint', { ns: 'companies' })}</div>
          <div>{t('whatsappWebhookVerifyTokenHint', { ns: 'companies' })}</div>
          <div>{t('whatsappSettingsStatus', {
            ns: 'companies',
            accessToken: whatsappAccountSettings?.hasCloudAccessToken ? t('configured', { ns: 'companies' }) : t('missing', { ns: 'companies' }),
            verifyToken: whatsappAccountSettings?.hasCloudWebhookVerifyToken ? t('configured', { ns: 'companies' }) : t('missing', { ns: 'companies' }),
            appSecret: whatsappAccountSettings?.hasCloudAppSecret ? t('configured', { ns: 'companies' }) : t('missing', { ns: 'companies' }),
          })}</div>
        </div>

        <div className="telegram-status-grid">
          <div className="telegram-status-card">
            <div className="telegram-status-card-title">{t('whatsappStatusCredentials', { ns: 'companies' })}</div>
            <dl className="telegram-status-list">
              <div>
                <dt>{t('whatsappStatusApiBaseUrl', { ns: 'companies' })}</dt>
                <dd>{whatsappAccountSettings?.cloudApiBaseUrl || whatsappSettingsForm.cloudApiBaseUrl || t('telegramStatusNotAvailable', { ns: 'companies' })}</dd>
              </div>
              <div>
                <dt>{t('whatsappStatusApiVersion', { ns: 'companies' })}</dt>
                <dd>{whatsappAccountSettings?.cloudApiVersion || whatsappSettingsForm.cloudApiVersion || t('telegramStatusNotAvailable', { ns: 'companies' })}</dd>
              </div>
              <div>
                <dt>{t('whatsappStatusAccountId', { ns: 'companies' })}</dt>
                <dd>{whatsappAccountSettings?.whatsAppAccountId || whatsappSettingsForm.whatsAppAccountId || t('telegramStatusNotAvailable', { ns: 'companies' })}</dd>
              </div>
              <div>
                <dt>{t('whatsappStatusCloudPhoneNumberId', { ns: 'companies' })}</dt>
                <dd>{whatsappAccountSettings?.cloudPhoneNumberId || whatsappSettingsForm.cloudPhoneNumberId || t('telegramStatusNotAvailable', { ns: 'companies' })}</dd>
              </div>
              <div>
                <dt>{t('whatsappStatusAccessToken', { ns: 'companies' })}</dt>
                <dd><ConnectorFieldStatus done={Boolean(whatsappAccountSettings?.hasCloudAccessToken)} /></dd>
              </div>
            </dl>
          </div>

          <div className="telegram-status-card">
            <div className="telegram-status-card-title">{t('whatsappStatusWebhook', { ns: 'companies' })}</div>
            <dl className="telegram-status-list">
              <div>
                <dt>{t('whatsappStatusWebhookUrl', { ns: 'companies' })}</dt>
                <dd>{whatsappWebhookUrl || t('telegramStatusNotAvailable', { ns: 'companies' })}</dd>
              </div>
              <div>
                <dt>{t('whatsappStatusVerifyToken', { ns: 'companies' })}</dt>
                <dd><ConnectorFieldStatus done={Boolean(whatsappAccountSettings?.hasCloudWebhookVerifyToken)} /></dd>
              </div>
              <div>
                <dt>{t('whatsappStatusSignatureValidation', { ns: 'companies' })}</dt>
                <dd><ConnectorFieldStatus done={Boolean(whatsappAccountSettings?.validateCloudSignature)} variant="enabled" /></dd>
              </div>
            </dl>
          </div>

          <div className="telegram-status-card">
            <div className="telegram-status-card-title">{t('whatsappStatusTestSend', { ns: 'companies' })}</div>
            {latestWhatsAppTestSendResult ? (
              <dl className="telegram-status-list">
                <div>
                  <dt>{t('whatsappStatusPhoneNumber', { ns: 'companies' })}</dt>
                  <dd>{latestWhatsAppTestSendResult.to}</dd>
                </div>
                <div>
                  <dt>{t('whatsappStatusMessageSid', { ns: 'companies' })}</dt>
                  <dd>{latestWhatsAppTestSendResult.messageSid}</dd>
                </div>
                <div>
                  <dt>{t('telegramStatusSentAt', { ns: 'companies' })}</dt>
                  <dd>{formatDateTime(latestWhatsAppTestSendResult.sentAt)}</dd>
                </div>
              </dl>
            ) : (
              <div className="telegram-status-empty">{t('whatsappStatusNoTestSend', { ns: 'companies' })}</div>
            )}
          </div>
        </div>

        <div className="company-actions-inline" style={{ marginTop: 16 }}>
          <button type="button" className="btn btn-primary" onClick={saveWhatsAppSettings} disabled={savingWhatsAppSettings || !hasWhatsAppSettingsChanges}>
            {savingWhatsAppSettings ? t('savingEllipsis', { ns: 'admin' }) : t('saveWhatsAppSettings', { ns: 'companies' })}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setWhatsappSettingsForm({
                cloudAccessToken: '',
                cloudWebhookVerifyToken: '',
                cloudAppSecret: '',
                cloudApiBaseUrl: whatsappAccountSettings?.cloudApiBaseUrl || 'https://graph.facebook.com',
                cloudApiVersion: whatsappAccountSettings?.cloudApiVersion || 'v25.0',
                whatsAppAccountId: whatsappAccountSettings?.whatsAppAccountId || '',
                cloudPhoneNumberId: whatsappAccountSettings?.cloudPhoneNumberId || '',
                phoneNumber: whatsappAccountSettings?.phoneNumber || '',
                validateCloudSignature: whatsappAccountSettings?.validateCloudSignature ?? false,
              });
              setWhatsappSettingsError(null);
              setWhatsappSettingsNotice(null);
            }}
            disabled={savingWhatsAppSettings}
          >
            {t('resetWhatsAppForm', { ns: 'companies' })}
          </button>
        </div>

        <div className="company-section" style={{ marginTop: 16 }}>
          <div className="company-section-head">
            <h3>{t('whatsappActivationSection', { ns: 'companies' })}</h3>
          </div>
          <p style={{ color: '#516173', marginTop: 0 }}>
            {t('whatsappActivationDescription', { ns: 'companies' })}
          </p>

          <div className="company-form-grid">
            <label>
              {t('whatsappRegisterPin', { ns: 'companies' })}
              <input
                className="text-input"
                value={whatsappRegisterPin}
                onChange={event => setWhatsappRegisterPin(event.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                inputMode="numeric"
                autoComplete="off"
              />
            </label>
          </div>
          <div style={{ color: '#516173', fontSize: 13, marginTop: 8 }}>
            {t('whatsappRegisterPinHint', { ns: 'companies' })}
          </div>

          <div className="company-actions-inline" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={registerWhatsAppNumberAction}
              disabled={registeringWhatsAppNumber
                || whatsappRegisterPin.trim().length !== 6
                || !whatsappAccountSettings?.hasCloudAccessToken
                || !whatsappAccountSettings?.cloudPhoneNumberId}
            >
              {registeringWhatsAppNumber ? t('savingEllipsis', { ns: 'admin' }) : t('registerWhatsAppNumber', { ns: 'companies' })}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={subscribeWhatsAppAppAction}
              disabled={subscribingWhatsAppApp
                || !whatsappAccountSettings?.hasCloudAccessToken
                || !whatsappAccountSettings?.whatsAppAccountId}
            >
              {subscribingWhatsAppApp ? t('savingEllipsis', { ns: 'admin' }) : t('subscribeWhatsAppApp', { ns: 'companies' })}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={unsubscribeWhatsAppAppAction}
              disabled={unsubscribingWhatsAppApp
                || !whatsappAccountSettings?.hasCloudAccessToken
                || !whatsappAccountSettings?.whatsAppAccountId}
            >
              {unsubscribingWhatsAppApp ? t('savingEllipsis', { ns: 'admin' }) : t('unsubscribeWhatsAppApp', { ns: 'companies' })}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={refreshWhatsAppSubscribedAppsAction}
              disabled={loadingWhatsAppSubscribedApps
                || !whatsappAccountSettings?.hasCloudAccessToken
                || !whatsappAccountSettings?.whatsAppAccountId}
            >
              {loadingWhatsAppSubscribedApps ? t('savingEllipsis', { ns: 'admin' }) : t('refreshWhatsAppSubscribedApps', { ns: 'companies' })}
            </button>
          </div>

          <div className="telegram-status-grid" style={{ marginTop: 16 }}>
            <div className="telegram-status-card">
              <div className="telegram-status-card-title">{t('whatsappStatusRegistration', { ns: 'companies' })}</div>
              {latestWhatsAppRegistration ? (
                <dl className="telegram-status-list">
                  <div>
                    <dt>{t('whatsappStatusCloudPhoneNumberId', { ns: 'companies' })}</dt>
                    <dd>{latestWhatsAppRegistration.cloudPhoneNumberId}</dd>
                  </div>
                  <div>
                    <dt>{t('whatsappStatusRegisteredAt', { ns: 'companies' })}</dt>
                    <dd>{formatDateTime(latestWhatsAppRegistration.registeredAtUtc)}</dd>
                  </div>
                </dl>
              ) : (
                <div className="telegram-status-empty">{t('whatsappStatusNoRegistration', { ns: 'companies' })}</div>
              )}
            </div>

            <div className="telegram-status-card">
              <div className="telegram-status-card-title">{t('whatsappStatusSubscribedApps', { ns: 'companies' })}</div>
              {loadingWhatsAppSubscribedApps ? (
                <div className="telegram-status-empty">{t('loadingWhatsAppSubscribedApps', { ns: 'companies' })}</div>
              ) : whatsappSubscribedApps?.items.length ? (
                <dl className="telegram-status-list">
                  {whatsappSubscribedApps.items.map((app, index) => (
                    <div key={app.appId || `${index}`}>
                      <dt>{app.appName || t('telegramStatusNotAvailable', { ns: 'companies' })}</dt>
                      <dd>{app.appId || t('telegramStatusNotAvailable', { ns: 'companies' })}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <div className="telegram-status-empty">{t('whatsappStatusNoSubscribedApps', { ns: 'companies' })}</div>
              )}
            </div>
          </div>
        </div>

        <div className="company-section" style={{ marginTop: 16 }}>
          <div className="company-section-head">
            <h3>{t('whatsappTestSendSection', { ns: 'companies' })}</h3>
          </div>
          <div className="company-form-grid">
            <label>
              {t('whatsappTestPhoneNumber', { ns: 'companies' })}
              <input
                className="text-input"
                value={whatsappTestPhoneNumber}
                onChange={event => setWhatsappTestPhoneNumber(event.target.value)}
                placeholder="+994501234567"
              />
            </label>
            <label>
              {t('whatsappTestMessage', { ns: 'companies' })}
              <input
                className="text-input"
                value={whatsappTestMessage}
                onChange={event => setWhatsappTestMessage(event.target.value)}
              />
            </label>
            <ToggleField
              label={t('whatsappTestPreviewUrl', { ns: 'companies' })}
              checked={whatsappTestPreviewUrl}
              onChange={setWhatsappTestPreviewUrl}
            />
          </div>
          <div style={{ color: '#516173', fontSize: 13, marginTop: 8 }}>
            {t('whatsappTestSendHint', { ns: 'companies' })}
          </div>
          <div className="company-actions-inline" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={sendWhatsAppTestMessageAction}
              disabled={testingWhatsAppSend || !whatsappTestPhoneNumber.trim() || !whatsappTestMessage.trim()}
            >
              {testingWhatsAppSend ? t('savingEllipsis', { ns: 'admin' }) : t('sendWhatsAppTestMessage', { ns: 'companies' })}
            </button>
          </div>
        </div>
      </>
    )
    ) : null}
  </section>
) : null}

{activeTab === 'instagram' ? (
  <section className="company-section">
    <div className="company-section-head">
      <h3>{t('instagramSettingsHeading', { ns: 'companies' })}</h3>
    </div>

    <p style={{ color: '#516173', marginTop: 0 }}>
      {t('instagramSettingsDescription', { ns: 'companies' })}
    </p>

    {instagramSettingsError ? <div className="error">{instagramSettingsError}</div> : null}
    {instagramSettingsNotice ? <div className="success">{instagramSettingsNotice}</div> : null}

    <ChannelAccountBar
      channel="Instagram"
      accounts={instagramChannelAccounts.accounts}
      limits={instagramChannelAccounts.limits}
      loading={instagramChannelAccounts.loading}
      error={instagramChannelAccounts.error}
      selectedAccountId={instagramChannelAccounts.selectedAccountId}
      onSelectAccount={instagramChannelAccounts.setSelectedAccountId}
      onRefresh={instagramChannelAccounts.reload}
      canManage={canManageConnectors}
      showAddForm={showAddInstagramAccount}
      onToggleAddForm={() => setShowAddInstagramAccount(previous => !previous)}
      onAccountUpdated={() => {
        const accountId = instagramChannelAccounts.selectedAccountId;
        if (accountId) {
          void loadInstagramAccountSettings(accountId);
        }
        void loadInstagramTenantSettings();
      }}
    />

    {showAddInstagramAccount ? (
      <AddInstagramAccountForm
        canManage={canManageConnectors}
        onCancel={() => setShowAddInstagramAccount(false)}
        onCreated={() => {
          setShowAddInstagramAccount(false);
          void instagramChannelAccounts.reload();
          showToast(t('channelAccountCreated', { ns: 'companies' }), 'success');
        }}
      />
    ) : null}

    {instagramChannelAccounts.accounts.length === 0 ? (
      <p style={{ color: '#516173', marginTop: 16 }}>
        {t('channelAccountCreateAccountForSettings', { ns: 'companies' })}
      </p>
    ) : instagramChannelAccounts.selectedAccountId ? (
      loadingInstagramSettings ? (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
        <Spinner text={t('loadingInstagramSettings', { ns: 'companies' })} />
      </div>
    ) : (
      <>
        <div className="company-section" style={{ marginBottom: 16 }}>
          <div className="company-section-head">
            <h3>{t('instagramOAuthConnectionHeading', { ns: 'companies' })}</h3>
          </div>
          <p style={{ color: '#516173', marginTop: 0 }}>
            {instagramTenantSettings?.connectedViaOAuth
              ? t('instagramOAuthConfigured', { ns: 'companies' })
              : t('instagramOAuthHelp', { ns: 'companies' })}
          </p>
          <div className="company-actions-inline" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={connectInstagramOAuth}
              disabled={connectingInstagramOAuth || savingInstagramSettings}
            >
              {connectingInstagramOAuth
                ? t('instagramOAuthConnecting', { ns: 'companies' })
                : instagramTenantSettings?.connectedViaOAuth
                  ? t('instagramOAuthReconnect', { ns: 'companies' })
                  : t('instagramOAuthConnect', { ns: 'companies' })}
            </button>
          </div>
        </div>

        <div className="company-section" style={{ marginBottom: 16 }}>
          <div className="company-section-head">
            <h3>{t('instagramOAuthSettingsHeading', { ns: 'companies' })}</h3>
          </div>
          <p style={{ color: '#516173', marginTop: 0 }}>
            {t('instagramOAuthSettingsHelp', { ns: 'companies' })}
          </p>
          <div style={{ color: '#516173', fontSize: 13, marginBottom: 12 }}>
            {t('instagramOAuthRedirectUriMetaHelp', { ns: 'companies' })}
          </div>
          <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
            <div style={{ color: '#516173', fontSize: 13 }}>
              {t('instagramOAuthRedirectUriRecommended', { ns: 'companies' })}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <code style={{ fontSize: 12, wordBreak: 'break-all' }}>
                {getDefaultInstagramOAuthRedirectUri(instagramWebhookUrl)}
              </code>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  void navigator.clipboard.writeText(getDefaultInstagramOAuthRedirectUri(instagramWebhookUrl));
                  showToast(t('instagramOAuthRedirectUriCopied', { ns: 'companies' }), 'success');
                }}
              >
                {t('instagramOAuthRedirectUriCopy', { ns: 'companies' })}
              </button>
            </div>
          </div>
          <div className="company-form-grid">
            <label>
              {t('instagramAppId', { ns: 'companies' })}
              <input
                className="text-input"
                value={instagramSettingsForm.appId || ''}
                onChange={event => setInstagramSettingsForm(prev => ({ ...prev, appId: event.target.value }))}
                placeholder={instagramTenantSettings?.hasAppId ? t('instagramAppIdConfigured', { ns: 'companies' }) : t('instagramAppIdPlaceholder', { ns: 'companies' })}
              />
            </label>

            <label>
              {t('instagramAppSecret', { ns: 'companies' })}
              <input
                className="text-input"
                type="password"
                value={instagramSettingsForm.appSecret || ''}
                onChange={event => setInstagramSettingsForm(prev => ({ ...prev, appSecret: event.target.value }))}
                placeholder={instagramTenantSettings?.hasAppSecret ? t('instagramAppSecretConfigured', { ns: 'companies' }) : t('instagramAppSecretPlaceholder', { ns: 'companies' })}
                autoComplete="new-password"
              />
            </label>

            <label>
              {t('instagramOAuthRedirectUri', { ns: 'companies' })}
              <input
                className="text-input"
                value={instagramSettingsForm.oauthRedirectUri || ''}
                onChange={event => setInstagramSettingsForm(prev => ({ ...prev, oauthRedirectUri: event.target.value }))}
                placeholder={getDefaultInstagramOAuthRedirectUri(instagramWebhookUrl)}
              />
            </label>

            <label>
              {t('instagramOAuthFrontendReturnUrl', { ns: 'companies' })}
              <input
                className="text-input"
                value={instagramSettingsForm.oauthFrontendReturnUrl || ''}
                onChange={event => setInstagramSettingsForm(prev => ({ ...prev, oauthFrontendReturnUrl: event.target.value }))}
                placeholder="https://dim-dev.simaizeer.org/admin/connectors?tab=instagram"
              />
            </label>
          </div>
        </div>

        <div className="company-section" style={{ marginBottom: 16 }}>
          <div className="company-section-head">
            <h3>{t('instagramAdvancedSettingsHeading', { ns: 'companies' })}</h3>
          </div>
          <div className="company-form-grid">
            <label>
              {t('instagramApiBaseUrl', { ns: 'companies' })}
              <input
                className="text-input"
                value={instagramSettingsForm.apiBaseUrl || ''}
                onChange={event => setInstagramSettingsForm(prev => ({ ...prev, apiBaseUrl: event.target.value }))}
                placeholder="https://graph.instagram.com"
              />
            </label>

            <label>
              {t('instagramApiVersion', { ns: 'companies' })}
              <input
                className="text-input"
                value={instagramSettingsForm.apiVersion || ''}
                onChange={event => setInstagramSettingsForm(prev => ({ ...prev, apiVersion: event.target.value }))}
                placeholder="v25.0"
              />
            </label>

            <label>
              {t('instagramBusinessAccountId', { ns: 'companies' })}
              <input
                className="text-input"
                value={instagramSettingsForm.instagramBusinessAccountId || ''}
                onChange={event => setInstagramSettingsForm(prev => ({ ...prev, instagramBusinessAccountId: event.target.value }))}
                placeholder="17841400000000000"
              />
            </label>

            <label>
              {t('instagramAccessToken', { ns: 'companies' })}
              <input
                className="text-input"
                type="password"
                value={instagramSettingsForm.pageAccessToken || ''}
                onChange={event => setInstagramSettingsForm(prev => ({ ...prev, pageAccessToken: event.target.value }))}
                placeholder={instagramAccountSettings?.hasPageAccessToken ? t('instagramAccessTokenConfigured', { ns: 'companies' }) : t('instagramAccessTokenPlaceholder', { ns: 'companies' })}
                autoComplete="new-password"
              />
            </label>

            <label>
              {t('instagramWebhookVerifyToken', { ns: 'companies' })}
              <input
                className="text-input"
                type="password"
                value={instagramSettingsForm.webhookVerifyToken || ''}
                onChange={event => setInstagramSettingsForm(prev => ({ ...prev, webhookVerifyToken: event.target.value }))}
                placeholder={instagramTenantSettings?.hasWebhookVerifyToken ? t('instagramWebhookVerifyTokenConfigured', { ns: 'companies' }) : t('instagramWebhookVerifyTokenPlaceholder', { ns: 'companies' })}
                autoComplete="new-password"
              />
            </label>

            <ToggleField
              label={t('instagramValidateSignature', { ns: 'companies' })}
              checked={Boolean(instagramSettingsForm.validateSignature)}
              onChange={value => setInstagramSettingsForm(prev => ({ ...prev, validateSignature: value }))}
            />
          </div>
        </div>

        <div className="company-section" style={{ marginBottom: 16 }}>
          <div className="company-section-head">
            <h3>{t('instagramCommentRepliesHeading', { ns: 'companies' })}</h3>
          </div>
          <p style={{ color: '#516173', marginTop: 0 }}>
            {t('instagramCommentRepliesHelp', { ns: 'companies' })}
          </p>
          <p style={{ color: '#516173', marginTop: 0, marginBottom: 12, fontSize: 13, lineHeight: 1.45 }}>
            {t('commentAutomationsCrossLink', { ns: 'companies' })}{' '}
            <Link to="/automations?tab=comments">
              {t('commentAutomationsCrossLinkCta', { ns: 'companies' })}
            </Link>
          </p>
          <div className={styles.instagramDefaultReplyModeBlock}>
            <div className={styles.instagramDefaultReplyModeLabel}>
              {t('instagramDefaultCommentReplyModeLabel', { ns: 'companies' })}
            </div>
            <div
              className={styles.instagramDefaultReplyModeToggle}
              role="group"
              aria-label={t('instagramDefaultCommentReplyModeLabel', { ns: 'companies' })}
            >
              {(['public_comment', 'private_comment'] as const).map(mode => (
                <button
                  key={mode}
                  type="button"
                  className={`${styles.instagramDefaultReplyModeButton} ${(instagramSettingsForm.defaultCommentReplyMode ?? 'public_comment') === mode ? styles.instagramDefaultReplyModeButtonActive : ''}`}
                  onClick={() => setInstagramSettingsForm(prev => ({ ...prev, defaultCommentReplyMode: mode }))}
                >
                  {t(`instagramDefaultCommentReplyMode_${mode}`, { ns: 'companies' })}
                </button>
              ))}
            </div>
            <div className={styles.instagramDefaultReplyModeHint}>
              {(instagramSettingsForm.defaultCommentReplyMode ?? 'public_comment') === 'private_comment'
                ? t('instagramDefaultCommentReplyModePrivateHint', { ns: 'companies' })
                : t('instagramDefaultCommentReplyModePublicHint', { ns: 'companies' })}
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gap: 6, marginTop: 12, color: '#516173', fontSize: 13 }}>
          <div>{t('instagramAccessTokenHint', { ns: 'companies' })}</div>
          <div>{t('instagramWebhookVerifyTokenHint', { ns: 'companies' })}</div>
          <div>{t('instagramSettingsStatus', {
            ns: 'companies',
            pageAccessToken: instagramAccountSettings?.hasPageAccessToken ? t('configured', { ns: 'companies' }) : t('missing', { ns: 'companies' }),
            verifyToken: instagramTenantSettings?.hasWebhookVerifyToken ? t('configured', { ns: 'companies' }) : t('missing', { ns: 'companies' }),
            appSecret: instagramTenantSettings?.hasAppSecret ? t('configured', { ns: 'companies' }) : t('missing', { ns: 'companies' }),
          })}</div>
        </div>

        <div className="telegram-status-grid">
          <div className="telegram-status-card">
            <div className="telegram-status-card-title">{t('instagramStatusConnection', { ns: 'companies' })}</div>
            <dl className="telegram-status-list">
              <div>
                <dt>{t('instagramStatusUsername', { ns: 'companies' })}</dt>
                <dd>{instagramAccountSettings?.instagramUsername || t('telegramStatusNotAvailable', { ns: 'companies' })}</dd>
              </div>
              <div>
                <dt>{t('instagramStatusBusinessAccountId', { ns: 'companies' })}</dt>
                <dd>{instagramAccountSettings?.instagramBusinessAccountId || instagramSettingsForm.instagramBusinessAccountId || t('telegramStatusNotAvailable', { ns: 'companies' })}</dd>
              </div>
              <div>
                <dt>{t('instagramStatusAccessToken', { ns: 'companies' })}</dt>
                <dd><ConnectorFieldStatus done={Boolean(instagramAccountSettings?.hasPageAccessToken)} /></dd>
              </div>
              <div>
                <dt>{t('instagramStatusTokenExpiry', { ns: 'companies' })}</dt>
                <dd>{formatDateTime(instagramTenantSettings?.accessTokenExpiresAt)}</dd>
              </div>
              <div>
                <dt>{t('instagramStatusLastRefresh', { ns: 'companies' })}</dt>
                <dd>{formatDateTime(instagramTenantSettings?.accessTokenRefreshedAt)}</dd>
              </div>
              <div>
                <dt>{t('instagramStatusConnectedViaOAuth', { ns: 'companies' })}</dt>
                <dd><ConnectorFieldStatus done={Boolean(instagramTenantSettings?.connectedViaOAuth)} variant="enabled" /></dd>
              </div>
            </dl>
          </div>

          <div className="telegram-status-card">
            <div className="telegram-status-card-title">{t('instagramStatusCredentials', { ns: 'companies' })}</div>
            <dl className="telegram-status-list">
              <div>
                <dt>{t('instagramStatusApiBaseUrl', { ns: 'companies' })}</dt>
                <dd>{instagramAccountSettings?.apiBaseUrl || instagramSettingsForm.apiBaseUrl || t('telegramStatusNotAvailable', { ns: 'companies' })}</dd>
              </div>
              <div>
                <dt>{t('instagramStatusApiVersion', { ns: 'companies' })}</dt>
                <dd>{instagramAccountSettings?.apiVersion || instagramSettingsForm.apiVersion || t('telegramStatusNotAvailable', { ns: 'companies' })}</dd>
              </div>
              <div>
                <dt>{t('instagramAppId', { ns: 'companies' })}</dt>
                <dd><ConnectorFieldStatus done={Boolean(instagramTenantSettings?.hasAppId)} /></dd>
              </div>
              <div>
                <dt>{t('instagramAppSecret', { ns: 'companies' })}</dt>
                <dd><ConnectorFieldStatus done={Boolean(instagramTenantSettings?.hasAppSecret)} /></dd>
              </div>
              <div>
                <dt>{t('instagramStatusOAuthRedirectUri', { ns: 'companies' })}</dt>
                <dd>{resolveInstagramOAuthRedirectUri() || t('telegramStatusNotAvailable', { ns: 'companies' })}</dd>
              </div>
            </dl>
          </div>

          <div className="telegram-status-card">
            <div className="telegram-status-card-title">{t('instagramStatusWebhook', { ns: 'companies' })}</div>
            <dl className="telegram-status-list">
              <div>
                <dt>{t('instagramStatusWebhookUrl', { ns: 'companies' })}</dt>
                <dd>{instagramWebhookUrl || t('telegramStatusNotAvailable', { ns: 'companies' })}</dd>
              </div>
              <div>
                <dt>{t('instagramStatusVerifyToken', { ns: 'companies' })}</dt>
                <dd><ConnectorFieldStatus done={Boolean(instagramTenantSettings?.hasWebhookVerifyToken)} /></dd>
              </div>
              <div>
                <dt>{t('instagramStatusSignatureValidation', { ns: 'companies' })}</dt>
                <dd><ConnectorFieldStatus done={Boolean(instagramTenantSettings?.validateSignature)} variant="enabled" /></dd>
              </div>
              <div>
                <dt>{t('instagramStatusDefaultCommentReplyMode', { ns: 'companies' })}</dt>
                <dd>
                  {t(`instagramDefaultCommentReplyMode_${instagramAccountSettings?.defaultCommentReplyMode ?? instagramSettingsForm.defaultCommentReplyMode ?? 'public_comment'}`, { ns: 'companies' })}
                </dd>
              </div>
              {loadingInstagramWebhookSubscriptions ? (
                <div>
                  <dt>{t('instagramStatusSubscribedFields', { ns: 'companies' })}</dt>
                  <dd>{t('loadingInstagramWebhookSubscriptions', { ns: 'companies' })}</dd>
                </div>
              ) : instagramWebhookSubscriptionStatus?.subscribedFields?.length ? (
                <div>
                  <dt>{t('instagramStatusSubscribedFields', { ns: 'companies' })}</dt>
                  <dd><ConnectorSubscribedFields fields={instagramWebhookSubscriptionStatus.subscribedFields} /></dd>
                </div>
              ) : null}
              {latestInstagramSubscriptionResult ? (
                <div>
                  <dt>{t('instagramStatusLastSubscription', { ns: 'companies' })}</dt>
                  <dd><ConnectorSubscribedFields fields={latestInstagramSubscriptionResult.subscribedFields} /></dd>
                </div>
              ) : null}
            </dl>
          </div>

          <div className="telegram-status-card">
            <div className="telegram-status-card-title">{t('instagramStatusTestSend', { ns: 'companies' })}</div>
            {latestInstagramTestSendResult ? (
              <dl className="telegram-status-list">
                <div>
                  <dt>{t('instagramStatusRecipientId', { ns: 'companies' })}</dt>
                  <dd>{latestInstagramTestSendResult.recipientId}</dd>
                </div>
                <div>
                  <dt>{t('instagramStatusMessageId', { ns: 'companies' })}</dt>
                  <dd>{latestInstagramTestSendResult.messageId}</dd>
                </div>
                <div>
                  <dt>{t('telegramStatusSentAt', { ns: 'companies' })}</dt>
                  <dd>{formatDateTime(latestInstagramTestSendResult.sentAtUtc)}</dd>
                </div>
              </dl>
            ) : (
              <div className="telegram-status-empty">{t('instagramStatusNoTestSend', { ns: 'companies' })}</div>
            )}
          </div>
        </div>

        <div className="company-actions-inline" style={{ marginTop: 16 }}>
          <button type="button" className="btn btn-primary" onClick={saveInstagramSettings} disabled={savingInstagramSettings || !hasInstagramSettingsChanges}>
            {savingInstagramSettings ? t('savingEllipsis', { ns: 'admin' }) : t('saveInstagramSettings', { ns: 'companies' })}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={subscribeInstagramWebhooksAction}
            disabled={subscribingInstagramWebhooks || !instagramAccountSettings?.hasPageAccessToken || !instagramAccountSettings?.instagramBusinessAccountId}
          >
            {subscribingInstagramWebhooks ? t('savingEllipsis', { ns: 'admin' }) : t('subscribeInstagramWebhooks', { ns: 'companies' })}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={unsubscribeInstagramWebhooksAction}
            disabled={unsubscribingInstagramWebhooks || !instagramAccountSettings?.hasPageAccessToken || !instagramAccountSettings?.instagramBusinessAccountId}
          >
            {unsubscribingInstagramWebhooks ? t('savingEllipsis', { ns: 'admin' }) : t('unsubscribeInstagramWebhooks', { ns: 'companies' })}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setInstagramSettingsForm({
                pageAccessToken: '',
                webhookVerifyToken: '',
                appId: '',
                appSecret: '',
                oauthRedirectUri: instagramTenantSettings?.oauthRedirectUri || instagramTenantSettings?.suggestedOAuthRedirectUri || '',
                oauthFrontendReturnUrl: instagramTenantSettings?.oauthFrontendReturnUrl || instagramTenantSettings?.suggestedOAuthFrontendReturnUrl || getDefaultInstagramOAuthFrontendReturnUrl(),
                apiBaseUrl: instagramAccountSettings?.apiBaseUrl || 'https://graph.instagram.com',
                apiVersion: instagramAccountSettings?.apiVersion || 'v25.0',
                instagramBusinessAccountId: instagramAccountSettings?.instagramBusinessAccountId || '',
                validateSignature: instagramTenantSettings?.validateSignature ?? false,
                defaultCommentReplyMode: (instagramAccountSettings?.defaultCommentReplyMode ?? 'public_comment') as 'public_comment' | 'private_comment',
              });
              setInstagramSettingsError(null);
              setInstagramSettingsNotice(null);
            }}
            disabled={savingInstagramSettings}
          >
            {t('resetInstagramForm', { ns: 'companies' })}
          </button>
        </div>

        <div className="company-section" style={{ marginTop: 16 }}>
          <div className="company-section-head">
            <h3>{t('instagramTestSendSection', { ns: 'companies' })}</h3>
          </div>
          <div className="company-form-grid">
            <label>
              {t('instagramTestRecipientId', { ns: 'companies' })}
              <input
                className="text-input"
                value={instagramTestRecipientId}
                onChange={event => setInstagramTestRecipientId(event.target.value)}
                placeholder="17841400000000000"
              />
            </label>
            <label>
              {t('instagramTestCommentId', { ns: 'companies' })}
              <input
                className="text-input"
                value={instagramTestCommentId}
                onChange={event => setInstagramTestCommentId(event.target.value)}
                placeholder="18100000000000000"
              />
            </label>
            <label>
              {t('instagramTestMessage', { ns: 'companies' })}
              <input
                className="text-input"
                value={instagramTestMessage}
                onChange={event => setInstagramTestMessage(event.target.value)}
              />
            </label>
          </div>
          <div style={{ color: '#516173', fontSize: 13, marginTop: 8 }}>
            {t('instagramTestSendHint', { ns: 'companies' })}
          </div>
          <div className="company-actions-inline" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={sendInstagramTestMessageAction}
              disabled={testingInstagramSend || (!instagramTestRecipientId.trim() && !instagramTestCommentId.trim()) || !instagramTestMessage.trim()}
            >
              {testingInstagramSend ? t('savingEllipsis', { ns: 'admin' }) : t('sendInstagramTestMessage', { ns: 'companies' })}
            </button>
          </div>
        </div>
      </>
    )
    ) : null}
  </section>
) : null}

{activeTab === 'messenger' ? (
  <MessengerConnectorTab canManage={canManageConnectors} />
) : null}

{activeTab === 'tiktok' ? (
  <TikTokConnectorTab
    canManage={canManageConnectors}
    onOAuthReturn={status => {
      if (status === 'success') {
        setActiveTab('tiktok');
      }
    }}
  />
) : null}

{activeTab === 'webchat' ? (
  <WebChatConnectorTab canManage={canManageConnectors} />
) : null}

{activeTab === 'calls' ? (
  <CallsConnectorTab canManage={canManageConnectors} />
) : null}

{activeTab === 'sms' ? (
  <SmsConnectorTab canManage={canManageConnectors} />
) : null}
      </div>
    </div>
  );
};

export default ConnectorsPage;
